import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  Switch,
  message,
  Breadcrumb,
  Divider,
  Row,
  Col,
  Spin,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  Typography,
  Modal,
  TimePicker,
  TreeSelect,
  Alert,
  Popover,
  Radio,
  Upload,
  Steps,
  Tag,
    Collapse,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  UserOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  FileSyncOutlined,
  CopyOutlined,
  SearchOutlined,
  FilterOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import {
  collectionAPIWithErrorHandling,
  collectionHelpers,
} from "../../../api/collection/collection";
import { collectionCategoryAPIWithErrorHandling } from "../../../api/collection/collectionCategory";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import { FixedSizeList as List } from "react-window";
import { groupCustomerAPIWithErrorHandling } from "../../../api/groupCustomer/groupCustomer";
import FeeTemplateModal from "../FeeTemplate/FeeTemplateModal";
import { paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";
import PaymentAccountSelector from "../PaymentAccount/PaymentAccountList";
import { cnfAPIWithErrorHandling } from "../../../api/invoice/cnf";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { downloadBlob } from "../../../utils/excel";
import "./CollectionForm.css";
import TransferContentInput from "./TransferContentInput";
import { assetAPIWithErrorHandling } from "../../../api/asset/asset";
import {
  useZeroFeeFilter,
  findZeroEntries,
  confirmZeroEntries,
  ZeroFeeFilterButton,
} from "./zeroFeeEntries";

const { TextArea } = Input;
const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Nhãn hiển thị trong file Excel, kèm mã ở cuối: "Tên tài sản [#3650]".
 *
 * Excel không có kiểu dropdown "hiện chữ, giữ mã ngầm" như web — ô chỉ lưu đúng chuỗi
 * người dùng chọn. Trước đây phải để mã ở một cột ẩn riêng, mà cột đó không đổi theo khi
 * người dùng chọn lại, nên tên và mã đá nhau. Nhét mã vào chính chuỗi thì chọn cái nào
 * là mã đi theo cái đó, không bao giờ lệch, và hai bản trùng tên cũng phân biệt được.
 */
const nhanCoMa = (ten, id) =>
    id === undefined || id === null || id === ""
        ? String(ten ?? "").trim()
        : `${String(ten ?? "").trim()} [#${id}]`;

/** Bóc nhãn "Tên [#12]" thành { ten, id }. Nhãn không mang mã (file cũ) thì id = null. */
const bocMa = (chuoi) => {
    const s = String(chuoi ?? "").trim();
    const khop = /^(.*?)\s*\[#(\d+)\]$/.exec(s);
    return khop ? { ten: khop[1].trim(), id: khop[2] } : { ten: s, id: null };
};

/**
 * Số thứ tự cột -> chữ cột Excel: 1=A … 26=Z, 27=AA, 28=AB.
 * Bản cũ dùng String.fromCharCode(64 + n) nên từ cột 27 trở đi sinh ra ký tự '[' và
 * làm hỏng công thức dropdown của cả file.
 */
const chuCot = (n) => {
    let s = "";
    let con = n;
    while (con > 0) {
        const du = (con - 1) % 26;
        s = String.fromCharCode(65 + du) + s;
        con = Math.floor((con - 1) / 26);
    }
    return s;
};



// ─── Ô nhập liệu khoản phí (FeeInputCell) ────────────────────────────────────
// Component riêng, debounce 300ms, commit khi blur/Enter
const FeeInputCell = React.memo(({ initialValue, disabled, onCommit }) => {
  const [localVal, setLocalVal] = React.useState(initialValue ?? 0);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    setLocalVal(initialValue ?? 0);
  }, [initialValue]);

  const commit = (val) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    onCommit(Number(val) || 0);
  };

  const handleChange = (val) => {
    setLocalVal(val ?? 0);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(val), 300); // Tăng lên 300ms để ổn định hơn
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <InputNumber
      min={0}
      max={999999999}
      value={localVal}
      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
      placeholder="0"
      controls={false}
      className="w-full collection-amount-input"
      disabled={disabled}
      style={{ width: "100%" }}
      onChange={handleChange}
      onBlur={() => commit(localVal)}
      onPressEnter={() => commit(localVal)}
    />
  );
});

// ─── Dòng dữ liệu đợt thu (CollectionRow) ─────────────────────────────────────
// Component riêng cho từng dòng trong bảng để tối ưu hóa render
/**
 * Chiều cao phần thân bảng khoản thu, co theo cửa sổ trình duyệt.
 *
 * Trước đây cố định 500px: màn 24 inch thừa cả khoảng trắng bên dưới mà vẫn chỉ xem
 * được 7 dòng, còn laptop nhỏ thì bảng lại tràn quá tầm nhìn. 420px là phần chừa cho
 * đầu trang, thẻ bộ lọc và thanh tổng kết bên dưới.
 */
const computeTableHeight = () =>
  Math.max(320, Math.min(700, window.innerHeight - 420));

const CollectionRow = React.memo(({ index, style, data }) => {
  const { tableDataSource, columns } = data;
  const record = tableDataSource[index];

  if (!record) return null;

  if (record.isHeader) {
    return (
      <div className="collection-group-header" style={style}>
        {/* Dính trái khi cuộn ngang, không thì kéo sang cột phí là mất tên khu phố,
            chỉ còn trơ dải màu */}
        <span
          style={{
            position: "sticky",
            left: 0,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <EnvironmentOutlined />
          {record.orgName} ({record.itemCount} khách hàng)
        </span>
      </div>
    );
  }

  return (
    <div
      className={`collection-table-row ${index % 2 === 0 ? "collection-table-row-even" : "collection-table-row-odd"}`}
      style={style}
    >
      {columns.map((col, colIdx) => (
        <div
          key={colIdx}
          style={{
            width: typeof col.width === "string" ? col.width : `${col.width}px`,
            minWidth:
              typeof col.width === "string" ? col.width : `${col.width}px`,
            flexShrink: 0,
            padding: "0 8px",
            // Bắt buộc phải khớp với ô tiêu đề (vốn đã border-box). Thiếu dòng này thì
            // mỗi ô phình thêm 16px tiền đệm, cả dòng rộng hơn hàng tiêu đề vài trăm
            // pixel và hai bên trượt lệch nhau dần khi cuộn ngang.
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            // Nền phải khớp đúng màu dòng chẵn/lẻ và phủ trọn chiều cao, không thì
            // nội dung các cột trượt qua bên dưới sẽ lộ ra ở mép trên và mép dưới.
            ...(col.fixed === "left" && {
              position: "sticky",
              left: 0,
              alignSelf: "stretch",
              backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
              zIndex: 9,
              borderRight: "1px solid #E2E8F0",
              boxShadow: "6px 0 6px -4px rgba(15, 23, 42, 0.10)",
            }),
            ...(col.fixed === "right" && {
              position: "sticky",
              right: 0,
              alignSelf: "stretch",
              backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
              zIndex: 9,
              borderLeft: "1px solid #E2E8F0",
              boxShadow: "-6px 0 6px -4px rgba(15, 23, 42, 0.10)",
            }),
            ...(col.align === "right" && {
              justifyContent: "flex-end",
            }),
            ...(col.align === "left" && {
              justifyContent: "flex-start",
            }),
            ...(col.align === "center" && {
              justifyContent: "center",
            }),
          }}
        >
          {col.render
            ? col.render(record[col.dataIndex], record, index)
            : record[col.dataIndex]}
        </div>
      ))}
    </div>
  );
});

// ─── Ô giảm trừ (DiscountCell) ────────────────────────────────────────────────
// Component riêng, tránh vòng lặp commit với lastCommittedRef
const DiscountCell = React.memo(
  ({ record, fees, entryValues, discountMode, isEditable, onCommit }) => {
    const eData = entryValues[record.id] || {};

    const subtotal = fees.reduce(
      (sum, fee) => sum + (Number(eData[fee.id]) || 0),
      0,
    );

    let autoDiscount = 0;
    if (record.customer_discount_type === "PERCENTAGE") {
      autoDiscount = Math.round(
        (subtotal * (Number(record.customer_discount_value) || 0)) / 100,
      );
    } else if (record.customer_discount_type === "FIXED") {
      autoDiscount = Number(record.customer_discount_value) || 0;
    }

    const currentMode = discountMode || "manual";
    const displayValue =
      currentMode === "auto"
        ? autoDiscount
        : Number(eData.discount_override) || 0;

    const [localVal, setLocalVal] = React.useState(displayValue);
    const lastCommittedRef = React.useRef(null);

    React.useEffect(() => {
      if (currentMode !== "auto") return;
      const currentStored =
        Number(entryValues[record.id]?.discount_override) || 0;
      if (
        autoDiscount !== currentStored &&
        autoDiscount !== lastCommittedRef.current
      ) {
        lastCommittedRef.current = autoDiscount;
        onCommit(autoDiscount);
        setLocalVal(autoDiscount);
      }
    }, [currentMode, autoDiscount]);

    React.useEffect(() => {
      if (currentMode === "manual") {
        lastCommittedRef.current = null;
        setLocalVal(Number(eData.discount_override) || 0);
      }
    }, [currentMode]);

    return (
      <InputNumber
        min={0}
        max={subtotal}
        value={localVal}
        disabled={currentMode === "auto" || !isEditable}
        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
        placeholder="0"
        controls={false}
        className="w-full collection-amount-input"
        style={{ width: "100%" }}
        onChange={(val) => setLocalVal(val ?? 0)}
        onBlur={() => {
          if (currentMode === "manual") {
            onCommit(Number(localVal) || 0);
          }
        }}
      />
    );
  },
);

// ─── StoreCollection ──────────────────────────────────────────────────────────
export default function StoreCollection() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isChildEditLocked = Form.useWatch("is_locked", form);
  const [loadingCus, setLoadingCus] = useState(false);
  const [loadingGroup, setLoadingGroup] = useState(false);
  // ── Step navigation (Wizard UI) ──
  const [currentStep, setCurrentStep] = useState(0);
  const { user } = useSelector((state) => state.persistedReducer.user);
  const userType = user?.data.info.type;
  const isStaffScopedUser = userType === "STAFF" || userType === "BRANCH_MANAGER";
  // Tiến bước với validation bước 1
  const handleNextStep = async () => {
    if (currentStep === 0) {
      try {
        await form.validateFields(['name', 'dateRange', 'collection_type', 'status']);
        setCurrentStep(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) { /* validation fail → ở lại bước 1 */ }
    } else if (currentStep === 1) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [submitting, setSubmitting] = useState(false);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [entries, setEntries] = useState([]);

  // ✅ entryValues: Nguồn dữ liệu chính cho tất cả giá trị trong bảng
  const [entryValues, setEntryValues] = useState({});

  const [filterOrgId, setFilterOrgId] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [sortByOrg, setSortByOrg] = useState(true);

  // Bảng khoản thu: hàng tiêu đề nằm ngoài vùng cuộn nên phải kéo nó chạy ngang theo
  // thân bảng. Đổi lại, thanh cuộn dọc ở ngay mép phải khung nhìn — không còn phải
  // kéo ngang hết bảng mới với tới được như trước.
  const tableHeaderRef = React.useRef(null);
  const tableBodyRef = React.useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState(computeTableHeight);

  // ── Luồng chọn theo Tài sản (Asset-centric) ──
  const ENABLE_ASSET_MODE = true; // Có thể bật/tắt tính năng chọn theo tài sản
  const [selectionMode, setSelectionMode] = useState("CUSTOMER"); // CUSTOMER | ASSET
  const [availableAssets, setAvailableAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Đơn vị của lần tải tài sản gần nhất. Bấm đổi đơn vị liên tiếp thì các request cũ vẫn
  // về — có khi về SAU request mới — và đè danh sách của đơn vị trước lên đơn vị đang
  // chọn; "Thêm tất cả" lúc đó gắn tài sản khối A vào khối B. Chỉ nhận kết quả khớp mã này.
  const assetRequestOrgRef = React.useRef(null);

  const fetchAvailableAssets = async (orgId) => {
    assetRequestOrgRef.current = orgId;
    const laKetQuaMoiNhat = () => assetRequestOrgRef.current === orgId;
    try {
      setLoadingAssets(true);
      const response = await assetAPIWithErrorHandling.getAssetAll(orgId);
      if (!laKetQuaMoiNhat()) return;
      const assets = response?.data?.data || [];
      // Chỉ lấy tài sản ĐÃ GÁN chủ sở hữu (customer_id) theo yêu cầu đại ca
      const validAssets = assets.filter(a => a.customer_id && a.customer);
      setAvailableAssets(validAssets);
      if (validAssets.length > 0) {
        message.info(`Tải thành công ${validAssets.length} tài sản đã gán chủ!`);
      }
    } catch (error) {
      if (laKetQuaMoiNhat()) {
        message.error("Không thể tải danh sách tài sản: " + error.message);
      }
    } finally {
      // Request cũ về trước không được tắt cờ tải — request mới vẫn đang chạy
      if (laKetQuaMoiNhat()) setLoadingAssets(false);
    }
  };

  const handleAssetSelect = (assetId) => {
    const asset = availableAssets.find(a => a.id === assetId);
    if (asset && asset.customer) {
        // Tự động map sang chủ sở hữu và gán id tài sản
        addEntry(asset.customer.id, asset.id, asset.customer);
        message.success(`Đã nhận diện chủ sở hữu: ${asset.customer.name}`);
    } else {
        message.warning("Tài sản này chưa được gán chính xác chủ sở hữu!");
    }
  };
  // ──────────────────────────────────────────────

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);

  const [tempFeeTax, setTempFeeTax] = useState(0);
  const [tempUnit, setTempUnit] = useState("");
  const [templateModalVisible, setTemplateModalVisible] = useState(false);

  const [fees, setFees] = useState([
    { id: "K1", name: "K1", tax: 0, unit: "" },
    { id: "K2", name: "K2", tax: 0, unit: "" },
    { id: "K3", name: "K3", tax: 0, unit: "" },
  ]);

  // Nghiệp vụ hiện chỉ chạy nhóm Dân cư. Hai nhóm kia khoá lại nhưng vẫn để trong
  // danh sách cho người dùng thấy là có tồn tại, chỉ tạm không dùng.
  const collectionTypes = [
    { label: "Khách hàng", value: "CUSTOMER", disabled: true },
    { label: "Bãi xe", value: "VEHICLE", disabled: true },
    { label: "Dân cư", value: "APARTMENT" },
  ];

  const collectionType = Form.useWatch("collection_type", form);

  const [editingFee, setEditingFee] = useState(null);
  const [feeNameModalVisible, setFeeNameModalVisible] = useState(false);
  const [tempFeeName, setTempFeeName] = useState("");
  const [tempFeePrice, setTempFeePrice] = useState(0);
  const [applyPriceToAll, setApplyPriceToAll] = useState(false);
  const [discountModes, setDiscountModes] = useState({});

  // ── Import state ──
  const [importFileList, setImportFileList] = useState([]);
  const [importSummaryVisible, setImportSummaryVisible] = useState(false);
  const [importSummary, setImportSummary] = useState({ added: 0, updated: 0, skipped: [] });

  const [valueOrg, setValueOrg] = useState();
  const [treeData, setTreeData] = useState([]);

  const [invoiceCnfs, setInvoiceCnfs] = useState([]);
  const [categories, setCollectionCategories] = useState([]);
  const [valueSendScheduleAt, setValueSendScheduleAt] = useState(null);
  const onChangeSendScheduleAt = (time) => setValueSendScheduleAt(time);

  // ── Hàm bổ trợ (Helpers) ──────────────────────────────────────────────────────

  const generateEntryId = () =>
    `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const updateEntryValue = (entryId, field, value) => {
    setEntryValues((prev) => ({
      ...prev,
      [entryId]: { ...(prev[entryId] || {}), [field]: value },
    }));
  };

  // ✅ Các hàm callback ổn định cho DiscountCell
  const handleDiscountCommit = useCallback((entryId, v) => {
    updateEntryValue(entryId, "discount_override", v);
    form.setFieldValue(["entryData", entryId, "discount_override"], v);
  }, []);

  const handleModeChange = useCallback((entryId, newMode) => {
    setDiscountModes((prev) => ({ ...prev, [entryId]: newMode }));
  }, []);

  // ── Vòng đời (Lifecycle) ──────────────────────────────────────────────────────

  useEffect(() => {
    loadOrganizations();
    fetchAccount();
    fetchInvoiceCnf();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (valueOrg) {
      fetchGroupCustomer(valueOrg);
      fetchAvailableCustomers(valueOrg, null);
    }
  }, [valueOrg]);

  // ── Gọi API (API calls) ───────────────────────────────────────────────────────

  const fetchInvoiceCnf = async () => {
    try {
      const response = await cnfAPIWithErrorHandling.getCnfAll();
      setInvoiceCnfs(response?.data?.data || []);
    } catch (error) {
      message.error(
        "Không thể tải danh sách tài khoản xuất hóa đơn: " + error.message,
      );
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await collectionCategoryAPIWithErrorHandling.getAllCategories();
      setCollectionCategories(response?.data?.data || []);
    } catch (error) {
      message.error("Không thể tải danh sách loại phí: " + error.message);
    }
  };

  const fetchAccount = async () => {
    try {
      const response = await paymentAccountAPIWithErrorHandling.getAccountAll();
      setAccounts(response?.data || []);
      setSelectedAccount(null);
    } catch (error) {
      message.error("Không thể tải danh sách tài khoản: " + error.message);
    }
  };

  const fetchGroupCustomer = async (id) => {
    try {
      setLoadingGroup(true);
      const response =
        await groupCustomerAPIWithErrorHandling.getGroupAllByOrg(id);
      setAvailableGroups(response?.data || []);
      setSelectedGroup(null);
    } catch (error) {
      message.error(
        "Không thể tải danh sách nhóm khách hàng: " + error.message,
      );
    } finally {
      setLoadingGroup(false);
    }
  };

  const fetchAvailableCustomers = async (id, groupId = null) => {
    try {
      setLoadingCus(true);
      let customersData = [];
      if (groupId) {
        const response =
          await groupCustomerAPIWithErrorHandling.getCustomerAllByGroup(
            groupId,
          );
        customersData = response.data || [];
        message.info(
          `Tải thành công ${customersData.length} khách hàng thuộc nhóm!`,
        );
      } else {
        const response =
          await organizationAPIWithErrorHandling.getCustomerAllByOrg(id);
        customersData = response.data || [];
        message.info(
          `Tải thành công ${customersData.length} khách hàng thuộc đơn vị!`,
        );
      }
      setAvailableCustomers(customersData);
    } catch (error) {
      message.error("Không thể tải danh sách khách hàng: " + error.message);
    } finally {
      setLoadingCus(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response =
        await organizationAPIWithErrorHandling.getTreeStructure();
      if (response.success) {
        setTreeData(flattenTreeData(response.data));
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const flattenTreeData = (nodes, parentId = 0) => {
    let result = [];
    nodes.forEach((node) => {
      result.push({
        id: node.id,
        pId: parentId,
        value: String(node.id),
        title: node.name,
        isLeaf: node.children_recursive?.length === 0,
      });
      if (node.children_recursive?.length > 0) {
        result = result.concat(
          flattenTreeData(node.children_recursive, node.id),
        );
      }
    });
    return result;
  };

  // ── Xử lý sự kiện (Event handlers) ─────────────────────────────────────────────

  const onChange = (newValOrg) => {
    setValueOrg(newValOrg);
    setAvailableCustomers([]);
    setAvailableAssets([]);
    setSelectedGroup(null);
    if (newValOrg && selectionMode === "ASSET") {
        fetchAvailableAssets(newValOrg);
    } else {
        // Bỏ chọn đơn vị / đang ở chế độ khách hàng: vô hiệu request tài sản còn treo
        assetRequestOrgRef.current = null;
        setLoadingAssets(false);
    }
  };

  const handleModeChangeSelection = (e) => {
    const mode = e.target.value;
    setSelectionMode(mode);
    if (mode === "ASSET" && valueOrg && availableAssets.length === 0) {
        fetchAvailableAssets(valueOrg);
    }
  };

  const handleGroupChange = (groupId) => {
    setSelectedGroup(groupId);
    if (groupId) {
      fetchAvailableCustomers(valueOrg, groupId);
    } else {
      fetchAvailableCustomers(valueOrg, null);
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates?.length === 2) {
      const startDate = dates[0];
      const suggestedName = collectionHelpers.generateCollectionName(
        startDate.month() + 1,
        startDate.year(),
      );
      if (!form.getFieldValue("name")) {
        form.setFieldValue("name", suggestedName);
      }
    }
  };

  // ✅ handleSelectAccount: dùng entryValues làm source of truth
  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    message.info(`Đã chọn tài khoản: ${account.name}`);

    let hasChange = false;
    const newEntryValues = { ...entryValues };

    // CHỈ điền cho dòng CHƯA có tài khoản. Trước đây còn đè lên dòng đang mang tài khoản
    // vừa bỏ chọn (`=== oldAccountId`), nên đợt gồm nhiều khối đơn vị là hỏng: chọn TK1
    // rồi thêm khối A, đổi sang TK2 để thêm khối B thì khối A bị kéo về TK2. Muốn đổi
    // hàng loạt thì dùng nút "Áp cho toàn bộ" / "Áp cho dòng đang lọc".
    entries.forEach((entry) => {
      const currentEntryAccId = entryValues[entry.id]?.acc_payment_id;
      if (!currentEntryAccId) {
        newEntryValues[entry.id] = {
          ...(entryValues[entry.id] || {}),
          acc_payment_id: account.id,
        };
        hasChange = true;
      }
    });

    if (hasChange) {
      const newFormData = {};
      Object.keys(newEntryValues).forEach((entryId) => {
        newFormData[entryId] = { ...newEntryValues[entryId] };
      });
      form.setFieldValue("entryData", newFormData);
      setEntryValues(newEntryValues);
      message.success(
        "Đã cập nhật tài khoản chung cho các dòng thu chưa chọn riêng",
      );
    }
  };

  /**
   * Áp tài khoản đang chọn cho một tập dòng — đường DUY NHẤT ghi đè tài khoản của dòng
   * đã có, để việc đổi lựa chọn ở ô trên đầu không âm thầm kéo cả danh sách.
   *
   * @param {string} pham_vi  "tat_ca" | "dang_loc"
   */
  const applyAccountTo = (pham_vi) => {
    if (!selectedAccount) {
      message.warning("Vui lòng chọn tài khoản trước");
      return;
    }

    // "dang_loc" bám đúng danh sách bảng đang hiển thị (đã trừ dòng tiêu đề nhóm)
    const dsDich =
      pham_vi === "dang_loc"
        ? tableDataSource.filter((item) => !item.isHeader)
        : entries;

    if (dsDich.length === 0) {
      message.info("Không có dòng nào để áp");
      return;
    }

    const newEntryValues = { ...entryValues };
    dsDich.forEach((entry) => {
      newEntryValues[entry.id] = {
        ...(entryValues[entry.id] || {}),
        acc_payment_id: selectedAccount.id,
      };
    });
    setEntryValues(newEntryValues);
    form.setFieldValue("entryData", { ...newEntryValues });

    message.success(`Đã áp "${selectedAccount.name}" cho ${dsDich.length} dòng`);
  };

  /** Thống kê tiền sẽ về tài khoản nào, để soát trước khi lưu. */
  const accountBreakdown = React.useMemo(() => {
    const dem = new Map();
    entries.forEach((entry) => {
      const accId = entryValues[entry.id]?.acc_payment_id || null;
      dem.set(accId, (dem.get(accId) || 0) + 1);
    });

    return [...dem.entries()].map(([accId, soDong]) => ({
      accId,
      soDong,
      ten: accounts.find((a) => a.id === accId)?.name || "Chưa chọn tài khoản",
    }));
  }, [entries, entryValues, accounts]);

  // ✅ handleExportTemplate: Xuất mẫu Excel nâng cao với Dropdown, Hidden Record ID, Sheet Protection
  const handleExportTemplate = async () => {
    if (entries.length === 0) {
      message.warning("Vui lòng thêm khách hàng vào bảng trước khi xuất mẫu để điền thông tin");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Dữ liệu nhập");
      const refSheet = workbook.addWorksheet("Tham chiếu (KHÔNG XÓA)");
      refSheet.state = "hidden";

      // ── Sheet Hướng dẫn ──
      const guideSheet = workbook.addWorksheet("Hướng dẫn");
      const guideData = [
        ["HƯỚNG DẪN SỬ DỤNG FILE NHẬP LIỆU"],
        [""],
        ["1. Các cột định dạng số/chữ xám (như ID Khách hàng, STT) KHÔNG được chỉnh sửa."],
        ["2. Cột 'Tài sản chọn': Click vào ô để hiện mũi tên dropdown, chọn tài sản hoặc nhập mới."],
        ["3. Cột 'Tài khoản nhận': Click vào ô để chọn tài khoản ngân hàng từ danh sách."],
        ["4. Cột khoản thu (K1, K2, K3...): Nhập số tiền, chỉ chấp nhận số nguyên ≥ 0."],
        ["5. Cột 'Giảm trừ': Nhập số tiền giảm trừ cho khách hàng, ≥ 0."],
        [""],
        ["⚠ LƯU Ý QUAN TRỌNG:"],
        ["- KHÔNG xóa hoặc thay đổi Sheet 'Tham chiếu'. File sẽ lỗi nếu Sheet này bị xóa."],
        ["- Cột 'Tài sản chọn' và 'Tài khoản nhận' hiển thị kèm mã ở cuối, dạng: Tên [#12]. Hãy CHỌN TỪ DANH SÁCH, đừng gõ tay, để giữ nguyên phần mã."],
        ["- Cột __record_id đã được tự động ẩn đi. KHÔNG hiện/xóa cột này."],
        ["- Mỗi khách hàng có thể xuất hiện nhiều dòng, nhưng mỗi dòng phải có tài sản KHÁC NHAU."],
        ["- Tối đa 1 dòng trống tài sản cho mỗi khách hàng."],
        ["- Nếu ô bị tô ĐỎ nghĩa là có trùng lặp cặp {Khách hàng - Tài sản}, cần sửa trước khi nhập."],
      ];
      guideData.forEach((row) => guideSheet.addRow(row));
      guideSheet.getRow(1).font = { bold: true, size: 14, color: { argb: "FF1890FF" } };
      guideSheet.getRow(9).font = { bold: true, color: { argb: "FFFF4D4F" } };
      guideSheet.getColumn(1).width = 90;

      // ── 1. Chuẩn bị danh sách tài khoản dùng chung ──
      const accountNames = accounts.map((a) => nhanCoMa(a.name, a.id));
      refSheet.getColumn(1).values = ["Danh sách tài khoản", ...accountNames];
      const accountRange = `'Tham chiếu (KHÔNG XÓA)'!$A$2:$A$${accountNames.length + 1}`;

      // ── 2. Chuẩn bị danh sách tài sản trên ref sheet (cột B trở đi) cho từng khách hàng có >255 ký tự ──
      const assetRefMap = {};
      let refColIndex = 2; // Cột B trở đi
      entries.forEach((entry, index) => {
        const customerAssets = (entry.assets || []).map((a) => nhanCoMa(a.name, a.id));
        if (customerAssets.length > 0) {
          const assetFormula = `"${customerAssets.join(",")}"`;
          if (assetFormula.length >= 255) {
            refSheet.getColumn(refColIndex).values = [
              `Tài sản KH ${entry.customer_id}`,
              ...customerAssets,
            ];
            assetRefMap[index] = {
              col: refColIndex,
              range: `'Tham chiếu (KHÔNG XÓA)'!$${chuCot(refColIndex)}$2:$${chuCot(refColIndex)}$${customerAssets.length + 1}`,
            };
            refColIndex++;
          }
        }
      });

      // ── 0. Thêm metadata row ──
      const collectionNameVal = form.getFieldValue('name') || 'Chưa đặt tên';
      const dateRangeVal = form.getFieldValue('dateRange');
      const periodStr = dateRangeVal
        ? `${dateRangeVal[0].format('DD/MM/YYYY')} – ${dateRangeVal[1].format('DD/MM/YYYY')}`
        : 'Chưa chọn';
      const metaRow = sheet.addRow([
        `Đợt thu: ${collectionNameVal}`,
        `Kỳ: ${periodStr}`,
        `Xuất ngày: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
        `Tổng khách hàng: ${entries.length}`,
      ]);
      metaRow.height = 22;
      metaRow.getCell(1).font = { bold: true, color: { argb: 'FF1890FF' }, size: 11 };
      metaRow.getCell(2).font = { color: { argb: 'FF555555' } };
      metaRow.getCell(3).font = { color: { argb: 'FF555555' } };
      metaRow.getCell(4).font = { bold: true, color: { argb: 'FF52C41A' } };
      sheet.addRow([]); // Hàng trống ngăn cách

      // ── 3. Định nghĩa Header cho sheet chính ──
      const feeColumns = fees.map((f) => f.name);
      const headerRow = [
        "STT",
        "ID Khách hàng (KHÔNG SỬA)",  // 2
        "Tên khách hàng",             // 3
        "Số điện thoại",              // 4
        "Đơn vị",                     // 5
        // Hai cột ID ẩn cũ đã bỏ: mã giờ nằm luôn trong nhãn, dạng "Tên [#12]", nên chọn
        // lại trong dropdown là mã đi theo, không còn cột nào để lệch.
        "Tài sản chọn (CHỌN TỪ DANH SÁCH)", // 6
        "Tài khoản nhận (CHỌN TỪ DANH SÁCH)", // 7
        ...feeColumns,                // 8+
        "Giảm trừ",                   // 8 + fees
        "__record_id",                // 9 + fees (ẩn)
      ];
      const RECORD_ID_COL = headerRow.length; 
      sheet.addRow(headerRow);

      // Style Header
      const headerRowObj = sheet.getRow(3);
      headerRowObj.font = { bold: true, color: { argb: "FF333333" } };
      headerRowObj.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      headerRowObj.height = 30;

      const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } };
      headerRow.forEach((_, idx) => {
        headerRowObj.getCell(idx + 1).fill = headerFill;
      });

      // ── 4. Đổ dữ liệu từng dòng ──
      const dupMap = new Map();

      entries.forEach((entry, index) => {
        const eId = entry.id;
        const eData = entryValues[eId] || {};
        const org = treeData.find((o) => Number(o.id) === Number(entry.organization_id));

        const currentAssetVal = eData.charge_target_number || entry.charge_target_number || "";
        const selectedAsset = (entry.assets || []).find((a) => String(a.id) === String(currentAssetVal));
        const assetName = selectedAsset ? selectedAsset.name : currentAssetVal;
        const assetId = selectedAsset ? selectedAsset.id : "";

        const account = accounts.find((a) => a.id === eData.acc_payment_id) || selectedAccount;

        const rowValues = [
          index + 1,
          entry.customer_id,
          entry.customer_name,
          entry.customer_phone,
          org?.title || "",
          // Nhãn mang mã: tài sản chưa có trong danh sách (người dùng gõ tay) thì không
          // có mã, cứ ghi nguyên chữ.
          selectedAsset ? nhanCoMa(assetName, assetId) : assetName,
          account ? nhanCoMa(account.name, account.id) : "",
          ...fees.map((f) => Number(eData[f.id]) || 0),
          Number(eData.discount_override) || 0,
          // __record_id (ẩn): ưu tiên id trong CSDL vì nó không đổi giữa các lần mở màn.
          // entry.id được sinh lại mỗi lần tải trang nên file xuất hôm nay mở lại ngày
          // mai sẽ không khớp dòng nào và bị coi là dòng mới. Màn thêm mới thì chưa có
          // db_id, dùng entry.id.
          entry.db_id ?? eId,
        ];
        const row = sheet.addRow(rowValues);
        const rowNum = index + 4;

        const normalizedAsset = String(assetName || "").trim().toLowerCase();
        const dupKey = `${entry.customer_id}~${normalizedAsset}`;
        if (!dupMap.has(dupKey)) dupMap.set(dupKey, []);
        dupMap.get(dupKey).push(rowNum);

        // Đổ màu font mờ cho các cột thông tin
        [1, 2, 3, 4, 5].forEach((cIdx) => {
          row.getCell(cIdx).font = { color: { argb: "FF666666" } };
        });

        // --- Dropdown Tài sản cho từng khách hàng ---
        const customerAssets = (entry.assets || []).map((a) => nhanCoMa(a.name, a.id));
        if (customerAssets.length > 0) {
          if (assetRefMap[index]) {
            row.getCell(6).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [assetRefMap[index].range],
              showErrorMessage: false,
            };
          } else {
            const assetFormula = `"${customerAssets.join(",")}"`;
            row.getCell(6).dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [assetFormula],
              showErrorMessage: false,
            };
          }
        }

        // --- Dropdown Tài khoản ngân hàng ---
        if (accountNames.length > 0) {
          row.getCell(7).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: [accountRange],
            showErrorMessage: false,
          };
        }
      });

      // ── 5. Conditional Formatting lỗi trùng lặp ──
      const errorFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFC7CE" } };
      const errorFont = { color: { argb: "FF9C0006" }, bold: true };
      dupMap.forEach((rowNums) => {
        if (rowNums.length > 1) {
          rowNums.forEach((rn) => {
            const dupCell = sheet.getRow(rn).getCell(6); // Cột Tài sản
            dupCell.fill = errorFill;
            dupCell.font = errorFont;
            dupCell.note = `⚠ Trùng lặp! Cùng khách hàng có ${rowNums.length} dòng với cùng tài sản.`;
          });
        }
      });

      // ── 6. Định dạng độ rộng cột (và ẩn ID thừa) ──
      sheet.columns = [
        { width: 5 },  // STT
        { width: 15 }, // ID Khách hàng
        { width: 25 }, // Tên
        { width: 15 }, // SĐT
        { width: 20 }, // Đơn vị
        { width: 42 }, // Tài sản (nhãn kèm mã)
        { width: 42 }, // Tài khoản nhận (nhãn kèm mã)
        ...fees.map(() => ({ width: 15 })),
        { width: 15 }, // Giảm trừ
        { width: 0.1, hidden: true }, // __record_id -> ẩn
      ];

      // ── 7. Sheet Protection ──
      // Chỉ cho phép sửa tài sản, tài khoản, số thu, giảm trừ. KH muốn xóa nền vàng/xám cho Excel trong suốt.
      // const dataRowCount = entries.length;
      // for (let r = 2; r <= dataRowCount + 1; r++) {
      //   const row = sheet.getRow(r);
      //   // Unlock tài sản(6), tk(8), fees(10+), discount
      //   const editableCols = [6, 8];
      //   for (let f = 0; f < fees.length; f++) editableCols.push(10 + f);
      //   editableCols.push(10 + fees.length);
        
      //   editableCols.forEach((colIdx) => {
      //     row.getCell(colIdx).protection = { locked: false };
      //   });
      // }

      // await sheet.protect("onepay2026", {
      //   selectLockedCells: true, //khóa bảng
      //   selectUnlockedCells: true,
      //   sort: true,
      //   autoFilter: true,
      //   formatCells: true,
      //   formatColumns: true,
      //   formatRows: true,
      //   insertColumns: true,
      //   insertRows: true, // Cho phép user thêm dòng mới tự do (cần cho KH trống tài sản)
      //   deleteColumns: true,
      //   deleteRows: true, // Cho phép user xóa dòng
      // });

      // ── 8. Xuất file ──
      const buffer = await workbook.xlsx.writeBuffer();
      downloadBlob(
        new Blob([buffer]),
        `Mau_nhap_Dropdown_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`,
      );
      message.success("Xuất mẫu Excel thành công!");
    } catch (error) {
      console.error("Export template error:", error);
      message.error("Có lỗi khi xuất mẫu Excel: " + error.message);
    }
  };

  // ✅ handleImportExcel: Đọc file Excel, Validate toàn diện, Upsert qua __record_id, Error mapping chi tiết
  const handleImportExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        // const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
        const ws = workbook.Sheets[sheetName];

        // ── Auto-detect header row: skip metadata rows ──
        // Thử parse từ row 1 trước (file cũ không có metadata)
        // Nếu không thấy cột "ID Khách hàng (KHÔNG SỬA)", thử từ row 3 (file mới có metadata)
        let rows = XLSX.utils.sheet_to_json(ws);
        // Dòng Excel thật của bản ghi đầu tiên, để mọi thông báo chỉ đúng chỗ cần sửa.
        // File do màn này xuất ra có metadata + dòng trống nên dữ liệu bắt đầu từ dòng 4;
        // báo theo dòng 2 là người dùng dò lệch 2 dòng.
        let firstDataRow = 2;
        if (rows.length > 0 && !Object.prototype.hasOwnProperty.call(rows[0], "ID Khách hàng (KHÔNG SỬA)")) {
          rows = XLSX.utils.sheet_to_json(ws, { range: 2 });
          firstDataRow = 4;
        }

        if (rows.length === 0) {
          message.error("File Excel không có dữ liệu");
          return;
        }

        // ── Bước 1: Pre-validation - Thu thập lỗi trước khi apply ──
        const validationErrors = [];
        const normalizedPairs = new Map(); // "cusId~normalizedAsset" -> { rows: [rowNum], customerName }

        rows.forEach((row, idx) => {
          const excelRow = idx + firstDataRow;
          const customerId = row["ID Khách hàng (KHÔNG SỬA)"];
          const customerPhone = String(row["Số điện thoại"] || "").trim();

          // TÌM KHÁCH HÀNG: Ưu tiên tìm trong entries trước (tránh lỗi khi availableCustomers bị load lại)
          let customer = null;
          let existingEntry = null;

          if (customerId) existingEntry = entries.find((e) => Number(e.customer_id) === Number(customerId));
          if (!existingEntry && customerPhone) existingEntry = entries.find((e) => String(e.customer_phone).trim() === customerPhone);

          if (existingEntry) {
            customer = {
              id: existingEntry.customer_id,
              name: existingEntry.customer_name,
              phone: existingEntry.customer_phone,
              organization_id: existingEntry.organization_id,
              assets: existingEntry.assets || [],
            };
          }

          // Fallback: Tìm trong danh sách KH available
          if (!customer) {
            if (customerId) customer = availableCustomers.find((c) => Number(c.id) === Number(customerId));
            if (!customer && customerPhone) customer = availableCustomers.find((c) => String(c.phone).trim() === customerPhone);
          }

          if (!customer) {
            validationErrors.push({
              row: excelRow,
              column: "ID Khách hàng",
              message: `Không tìm thấy khách hàng với ID '${customerId || ""}' hoặc SĐT '${customerPhone}'`,
            });
            return;
          }

          // Normalize tài sản
          const assetIdFromExcel = row["ID Tài sản (AUTO)"];
          const assetNameRaw = String(row["Tài sản chọn (CHỌN TỪ DANH SÁCH)"] || "").trim();

          
          // let assetDisplayName = "";
          // if (assetIdFromExcel !== undefined && assetIdFromExcel !== "") {
          //   const matchedById = (customer.assets || []).find((a) => String(a.id) === String(assetIdFromExcel));
          //   assetDisplayName = matchedById ? matchedById.name : String(assetIdFromExcel);
          // } else if (assetNameRaw) {
          //   assetDisplayName = assetNameRaw;
          // }

          let assetDisplayName = "";
          if (assetNameRaw) {
            const matchedAsset = (customer.assets || []).find((a) => (a.name || "").trim().toLowerCase() === assetNameRaw.toLowerCase());
            assetDisplayName = matchedAsset ? matchedAsset.name : assetNameRaw;
          } else if (assetIdFromExcel !== undefined && assetIdFromExcel !== "") {
            // User xóa trống ô hiển thị Tài sản, coi như cố ý clear tài sản dù cột ẩn có ID (do copy paste Excel sinh ra)
            assetDisplayName = "";
          }

          // Normalized key for duplicate detection
          const normalizedAsset = assetDisplayName.trim().toLowerCase();
          const dupKey = `${customer.id}~${normalizedAsset}`;

          if (!normalizedPairs.has(dupKey)) {
            normalizedPairs.set(dupKey, { rows: [], customerName: customer.name, asset: assetDisplayName });
          }
          normalizedPairs.get(dupKey).rows.push(excelRow);
        });

        // Check duplicate pairs
        normalizedPairs.forEach((info, key) => {
          if (info.rows.length > 1) {
            // Cho phép tối đa 1 dòng trống tài sản
            const isEmptyAsset = key.endsWith("~");
            if (isEmptyAsset && info.rows.length === 2) {
              validationErrors.push({
                row: info.rows[1],
                column: "Tài sản",
                message: `Khách hàng '${info.customerName}' đã có 1 dòng không có tài sản (dòng ${info.rows[0]}). Tối đa 1 dòng trống tài sản.`,
              });
            } else if (isEmptyAsset && info.rows.length > 2) {
              info.rows.slice(1).forEach((rn) => {
                validationErrors.push({
                  row: rn,
                  column: "Tài sản",
                  message: `Khách hàng '${info.customerName}' có ${info.rows.length} dòng trống tài sản (dòng ${info.rows.join(", ")}). Tối đa 1 dòng.`,
                });
              });
            } else if (!isEmptyAsset) {
              info.rows.slice(1).forEach((rn) => {
                validationErrors.push({
                  row: rn,
                  column: "Tài sản",
                  message: `Tài sản '${info.asset}' bị trùng với dòng ${info.rows[0]} của cùng khách hàng '${info.customerName}'`,
                });
              });
            }
          }
        });

        // Validate số tiền
        rows.forEach((row, idx) => {
          const excelRow = idx + firstDataRow;
          fees.forEach((fee) => {
            const val = row[fee.name];
            if (val !== undefined && val !== "") {
              const num = Number(val);
              if (isNaN(num) || num < 0) {
                validationErrors.push({
                  row: excelRow,
                  column: fee.name,
                  message: `Giá trị '${val}' không hợp lệ. Phải là số ≥ 0.`,
                });
              }
            }
          });
          const discountVal = row["Giảm trừ"];
          if (discountVal !== undefined && discountVal !== "") {
            const num = Number(discountVal);
            if (isNaN(num) || num < 0) {
              validationErrors.push({
                row: excelRow,
                column: "Giảm trừ",
                message: `Giá trị giảm trừ '${discountVal}' không hợp lệ. Phải là số ≥ 0.`,
              });
            }
          }
        });

        if (validationErrors.length > 0) {
          validationErrors.sort((a, b) => a.row - b.row);
          Modal.error({
            title: `Phát hiện ${validationErrors.length} lỗi dữ liệu`,
            width: 700,
            content: (
              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                <Alert
                  message="Vui lòng sửa các lỗi sau trong file Excel rồi nhập lại."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                      <th style={{ padding: "8px", textAlign: "left", width: 60 }}>Dòng</th>
                      <th style={{ padding: "8px", textAlign: "left", width: 120 }}>Cột</th>
                      <th style={{ padding: "8px", textAlign: "left" }}>Lý do lỗi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationErrors.map((err, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}>
                        <td style={{ padding: "6px 8px", color: "#E5065C", fontWeight: 600 }}>{err.row}</td>
                        <td style={{ padding: "6px 8px", fontWeight: 500 }}>{err.column}</td>
                        <td style={{ padding: "6px 8px" }}>{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          });
          return;
        }

        // ── Bước 2: Apply data ──
        const newEntries = [...entries];
        const newEntryValues = { ...entryValues };
        const newDiscountModes = { ...discountModes };
        let addedCount = 0;
        let updatedCount = 0;
        const usedRecordIds = new Set(); // Theo dõi ID đã dùng (chống copy paste trùng __record_id trong Excel)
        // Đếm dòng mà cột tên và cột ID ẩn đá nhau — hệ thống nghe theo tên, và phải nói
        // cho người nhập biết là nó đã hiểu đúng ý họ.
        let accChangedByName = 0;
        let accNameNotFound = 0;
        // Đếm dòng chọn tài sản thuộc đơn vị KHÁC đơn vị của dòng thu. Danh sách tài
        // sản trong file là toàn bộ tài sản của khách, không lọc theo đơn vị, nên khách
        // có căn ở nhiều khối rất dễ chọn nhầm sang khối khác — mà cột "Đơn vị" trong
        // file chỉ để xem, nhập lại không đổi được đơn vị của dòng.
        const assetKhacDonVi = [];

        const CHUNK_SIZE = 100;
        const processChunk = (startIdx) => {
          const endIdx = Math.min(startIdx + CHUNK_SIZE, rows.length);

          for (let i = startIdx; i < endIdx; i++) {
            const row = rows[i];
            const customerId = row["ID Khách hàng (KHÔNG SỬA)"];
            const customerPhone = String(row["Số điện thoại"] || "").trim();
            // Excel trả về số cho db_id và chuỗi cho entry.id, nên so sánh bằng chuỗi
            // cho mọi trường hợp.
            const rawRecordId = row["__record_id"];
            let recordId =
              rawRecordId === undefined || rawRecordId === null || rawRecordId === ""
                ? null
                : String(rawRecordId).trim();

            // Nếu recordId đã được dùng ở dòng trước (do user copy-paste ở Excel) -> coi như dòng mới
            if (recordId) {
              if (usedRecordIds.has(recordId)) {
                recordId = null;
              } else {
                usedRecordIds.add(recordId);
              }
            }

            let customer = null;
            let existingEntry = null;

            if (customerId) existingEntry = newEntries.find((e) => Number(e.customer_id) === Number(customerId));
            if (!existingEntry && customerPhone) existingEntry = newEntries.find((e) => String(e.customer_phone).trim() === customerPhone);

            if (existingEntry) {
              customer = {
                id: existingEntry.customer_id,
                name: existingEntry.customer_name,
                phone: existingEntry.customer_phone,
                organization_id: existingEntry.organization_id,
                assets: existingEntry.assets || [],
              };
            }

            if (!customer) {
              if (customerId) customer = availableCustomers.find((c) => Number(c.id) === Number(customerId));
              if (!customer && customerPhone) customer = availableCustomers.find((c) => String(c.phone).trim() === customerPhone);
            }
            if (!customer) continue;

            const assetIdFromExcel = row["ID Tài sản (AUTO)"];
            const assetNameFromExcel = String(row["Tài sản chọn (CHỌN TỪ DANH SÁCH)"] || "").trim();

            // Nhãn dạng "Tên [#12]": mã nằm ngay trong ô nên chọn lại trong dropdown là
            // mã đi theo — không còn cột ẩn nào để lệch.
            const assetNhan = bocMa(assetNameFromExcel);

            let assetVal = "";
            if (assetNhan.id) {
              const theoMa = (customer.assets || []).find((a) => String(a.id) === String(assetNhan.id));
              assetVal = theoMa ? theoMa.id : assetNhan.ten;
            } else if (assetNameFromExcel) {
              // File cũ (nhãn chưa mang mã) hoặc người dùng gõ tay: giữ đường dò cũ
              const normalizedInput = assetNhan.ten.toLowerCase();
              const customerAssets = customer.assets || [];

              // Cột ID ẩn chỉ được tin khi TÊN trong ô còn khớp với tài sản mang id đó,
              // và tài sản đó đúng là của khách ở dòng này. Dropdown trong Excel chỉ đổi
              // tên, ô ID vẫn giữ giá trị cũ — tin ID vô điều kiện là nuốt mất thao tác
              // đổi tài sản.
              const assetById =
                assetIdFromExcel !== undefined && assetIdFromExcel !== ""
                  ? customerAssets.find((a) => String(a.id) === String(assetIdFromExcel))
                  : null;

              if (assetById && (assetById.name || "").trim().toLowerCase() === normalizedInput) {
                // Khớp cả id lẫn tên thì lấy thẳng id, khỏi dò theo tên và khỏi vớ nhầm
                // tài sản trùng tên của cùng khách.
                assetVal = assetById.id;
              } else {
                const matchedAsset = customerAssets.find(
                  (a) => (a.name || "").trim().toLowerCase() === normalizedInput,
                );
                assetVal = matchedAsset ? matchedAsset.id : assetNameFromExcel;
              }
            } else if (assetIdFromExcel !== undefined && assetIdFromExcel !== "") {
              // User xóa trống ô hiển thị Tài sản, coi như cố ý clear tài sản (bỏ qua cột ẩn copy dư)
              assetVal = "";
            }

            const accountIdFromExcel = row["ID Tài khoản (AUTO)"];
            const accountNameFromExcel = row["Tài khoản nhận (CHỌN TỪ DANH SÁCH)"];

            let entry = null;

            if (recordId) {
              // Dò theo id trong CSDL trước — đây là thứ file mang theo và là thứ duy
              // nhất không đổi giữa các lần mở màn. Chỉ khi không có mới dò theo
              // entry.id, để file xuất trong cùng phiên (dòng chưa lưu) vẫn khớp.
              entry =
                newEntries.find(
                  (e) => e.db_id != null && String(e.db_id) === recordId
                ) || newEntries.find((e) => String(e.id) === recordId);

              // Dòng tìm được phải đúng khách của dòng đang đọc. __record_id là cột ẩn:
              // người dùng sao chép một dòng để tạo dòng cho khách khác, sửa tên/SĐT/ID
              // khách nhưng không thấy mà xoá cột này — thiếu chốt ở đây là tiền của
              // khách mới ghi đè vào dòng khách cũ.
              if (entry && String(entry.customer_id) !== String(customer.id)) {
                entry = null;
              }
            }

            if (!entry) {
              entry = newEntries.find(
                (e) =>
                  String(e.customer_id) === String(customer.id) &&
                  (String(e.asset_info || "") === String(assetVal || "") || String(e.charge_target_number || "") === String(assetVal || "") || String(e.asset_id || "") === String(assetVal || ""))
              );
            }

            if (!entry) {
              const entryId = generateEntryId();
              entry = {
                id: entryId,
                customer_id: customer.id,
                customer_name: customer.name,
                customer_phone: customer.phone,
                customer_discount_type: customer.discount_type ?? null,
                customer_discount_value: customer.discount_value ?? 0,
                organization_id: customer.organization_id || valueOrg,
                charge_target_number: assetVal,
                asset_info: assetVal,
                assets: customer.assets || [],
              };
              newEntries.push(entry);
              newDiscountModes[entryId] = "manual";
              addedCount++;
            } else {
              updatedCount++;
            }

            const entryId = entry.id;
            const eData = { ...(newEntryValues[entryId] || {}) };
            eData.charge_target_number = assetVal;
            eData.customer_id = customer.id;

            fees.forEach((fee) => {
              if (row[fee.name] !== undefined) {
                eData[fee.id] = Number(row[fee.name]) || 0;
              }
            });

            if (row["Giảm trừ"] !== undefined) {
              eData.discount_override = Number(row["Giảm trừ"]) || 0;
              newDiscountModes[entryId] = "manual";
            }

            // Cột ID ẩn chỉ được tin khi TÊN trong ô còn khớp với tài khoản mang id đó.
            // Dropdown trong Excel chỉ đổi cột tên, cột ID bị ẩn nên giữ nguyên giá trị
            // cũ — tin ID vô điều kiện là nuốt mất thao tác đổi tài khoản của người dùng.
            // Nhãn dạng "Tên [#12]" — ưu tiên mã nằm trong chính ô
            const accNhan = bocMa(accountNameFromExcel);
            const accTheoMa = accNhan.id
              ? accounts.find((a) => String(a.id) === String(accNhan.id))
              : null;

            const accNameKey = accNhan.ten.toLowerCase();
            const accById =
              accountIdFromExcel !== undefined && accountIdFromExcel !== ""
                ? accounts.find((a) => String(a.id) === String(accountIdFromExcel))
                : null;

            if (accTheoMa) {
              // Mã đi kèm ngay trong nhãn: tin tuyệt đối
              eData.acc_payment_id = accTheoMa.id;
            } else if (accById && (accById.name || "").trim().toLowerCase() === accNameKey) {
              // File cũ: cột tên và cột ID ẩn còn khớp nhau
              eData.acc_payment_id = accById.id;
            } else if (accNameKey) {
              // Hai cột đá nhau, hoặc ô ID trống: người dùng vừa đổi tay nên nghe theo TÊN
              const matchedAcc = accounts.find(
                (a) => (a.name || "").trim().toLowerCase() === accNameKey,
              );
              if (matchedAcc) {
                if (accById && matchedAcc.id !== accById.id) accChangedByName++;
                eData.acc_payment_id = matchedAcc.id;
              } else {
                // Gõ/dán tên không có trong danh sách: giữ tài khoản cũ cho dòng vẫn sinh
                // được QR, nhưng phải đếm để báo — im lặng giữ nguyên là người nhập tưởng
                // đã đổi được.
                accNameNotFound++;
                if (accById) eData.acc_payment_id = accById.id;
              }
            } else if (accById) {
              // Ô tên bị xoá trắng mà ID còn: giữ nguyên tài khoản cũ, vì dòng nào cũng
              // phải có tài khoản mới sinh được mã QR
              eData.acc_payment_id = accById.id;
            }

            // Soát đơn vị của tài sản so với đơn vị của dòng. Chỉ soát khi biết chắc cả
            // hai — tài sản gõ tay hoặc dữ liệu cũ thiếu organization_id thì bỏ qua.
            const assetDaChon = (customer.assets || []).find(
              (a) => String(a.id) === String(assetVal),
            );
            const donViDong = entry.organization_id || valueOrg;
            if (
              assetDaChon?.organization_id &&
              donViDong &&
              String(assetDaChon.organization_id) !== String(donViDong)
            ) {
              assetKhacDonVi.push({
                row: i + firstDataRow,
                customer: entry.customer_name || customer.name,
                asset: assetDaChon.name,
                orgAsset:
                  treeData.find((o) => Number(o.id) === Number(assetDaChon.organization_id))?.title
                  || `đơn vị #${assetDaChon.organization_id}`,
              });
            }

            newEntryValues[entryId] = eData;
          }

          if (endIdx < rows.length) {
            // Lô sau chạy ngoài try/catch bên ngoài: lỗi giữa chừng mà không bắt là thông
            // báo "Đang xử lý" treo mãi
            setTimeout(() => {
              try {
                processChunk(endIdx);
              } catch (error) {
                console.error("Import error:", error);
                message.destroy("importProgress");
                message.error("Lỗi khi xử lý file Excel, dữ liệu chưa được nhập.");
              }
            }, 0);
          } else {
            message.destroy("importProgress");
            setEntries(newEntries);
            setEntryValues(newEntryValues);
            setDiscountModes(newDiscountModes);
            form.setFieldValue("entryData", newEntryValues);
            // Reset upload file để có thể nhập lại cùng file
            setImportFileList([]);
            // Hiện bảng tóm tắt thay vì message đơn giản
            setImportSummary({ added: addedCount, updated: updatedCount, skipped: [] });
            setImportSummaryVisible(true);

            // Nói rõ khi file có dòng sửa tên tài khoản: người nhập cần biết hệ thống đã
            // nghe theo tên chứ không giữ tài khoản cũ.
            if (accChangedByName > 0) {
              message.info(`Đã đổi tài khoản nhận theo tên trong file cho ${accChangedByName} dòng`);
            }
            if (accNameNotFound > 0) {
              message.warning(
                `${accNameNotFound} dòng ghi tên tài khoản không có trong danh sách, đã giữ nguyên tài khoản cũ`,
              );
            }
            if (assetKhacDonVi.length > 0) {
              // Không chặn, chỉ nói rõ: thu hộ căn thuộc khối khác có thể là hợp lệ,
              // nhưng không được để lệch mà không ai biết.
              Modal.warning({
                title: `${assetKhacDonVi.length} dòng có tài sản thuộc đơn vị khác`,
                width: 640,
                content: (
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      Đơn vị của dòng thu vẫn giữ theo đợt thu. Kiểm lại nếu đây không phải ý bạn:
                    </div>
                    <div style={{ maxHeight: 260, overflowY: "auto", fontSize: 13 }}>
                      {assetKhacDonVi.slice(0, 50).map((item, i) => (
                        <div key={i} style={{ padding: "3px 0", borderTop: i === 0 ? "none" : "1px dashed #F1F5F9" }}>
                          <b>Dòng {item.row}</b> — {item.customer}: tài sản{" "}
                          <b>{item.asset}</b> thuộc <b>{item.orgAsset}</b>
                        </div>
                      ))}
                      {assetKhacDonVi.length > 50 && (
                        <div style={{ marginTop: 6, color: "#64748B" }}>
                          … và {assetKhacDonVi.length - 50} dòng nữa
                        </div>
                      )}
                    </div>
                  </div>
                ),
              });
            }
          }
        };

        if (rows.length > CHUNK_SIZE) {
          // Tắt ở lô cuối của processChunk, không hẹn giờ: 6.000 dòng chạy lâu hơn mốc ước
          // tính, thông báo tắt sớm là người nhập tưởng xong mà bảng chưa có tiền.
          message.loading({ content: `Đang xử lý ${rows.length} dòng dữ liệu...`, key: "importProgress", duration: 0 });
          processChunk(0);
        } else {
          processChunk(0);
        }
      } catch (error) {
        console.error("Import error:", error);
        message.destroy("importProgress");
        message.error("Lỗi khi đọc file Excel. Vui lòng kiểm tra định dạng file mẫu.");
      }
    };
    reader.readAsBinaryString(file);
    return false;
  };

  // ── Quản lý dòng thu (Entry management) ────────────────────────────────────────

  /* ================== ĐOẠN CODE CŨ (ĐÃ YÊU CẦU COMMENT LẠI) ==================
  // ✅ addEntry (OLD)
  const addEntry = (customerId, assetInfo = "") => {
    if (!customerId) {
      message.warning("Vui lòng chọn khách hàng");
      return;
    }
    if (!selectedAccount) {
      message.warning("Vui lòng chọn tài khoản thanh toán trước");
      return;
    }

    const customer = availableCustomers.find((c) => c.id === customerId);
    if (!customer) {
      message.error("Không tìm thấy khách hàng");
      return;
    }
    // ... logic giống nhau phần dưới
  };
  ============================================================================= */

  // ✅ addEntry: sync cả form và entryValues (NEW)
  const addEntry = (customerId, assetInfo = "", fallbackCustomer = null) => {
    if (!customerId) {
      message.warning("Vui lòng chọn khách hàng");
      return;
    }
    if (!selectedAccount) {
      message.warning("Vui lòng chọn tài khoản thanh toán trước");
      return;
    }

    let customer = availableCustomers.find((c) => c.id === customerId);
    if (!customer) {
      if (fallbackCustomer) {
        customer = fallbackCustomer;
      } else {
        message.error("Không tìm thấy khách hàng");
        return;
      }
    }

    const isDuplicate = entries.some(
      (e) =>
        Number(e.customer_id) === Number(customerId) &&
        (String(e.asset_info) === String(assetInfo) || String(e.charge_target_number) === String(assetInfo) || String(e.asset_id) === String(assetInfo)) &&
        Number(e.organization_id) === Number(valueOrg),
    );
    if (isDuplicate) {
      message.warning("Dòng thu này đã tồn tại (cùng khách hàng và tài sản)");
      return;
    }

    const entryId = generateEntryId();
    const newEntry = {
      id: entryId,
      customer_id: customerId,
      customer_name: customer.name,
      customer_phone: customer.phone,
      customer_discount_type: customer.discount_type ?? null,
      customer_discount_value: customer.discount_value ?? 0,
      organization_id: valueOrg ?? null,
      charge_target_number: assetInfo,
      asset_info: assetInfo,
      assets: customer.assets || [],
    };

    const newEntryData = {
      discount_override: 0,
      charge_target_number: assetInfo,
      acc_payment_id: selectedAccount.id,
    };
    fees.forEach((fee) => {
      newEntryData[fee.id] = fee.price || 0;
    });
    if (customer.discount_type === "FIXED") {
      newEntryData.discount_override = Number(customer.discount_value) || 0;
    }

    setEntries((prev) => [...prev, newEntry]);
    setDiscountModes((prev) => ({ ...prev, [entryId]: "auto" }));
    setEntryValues((prev) => ({ ...prev, [entryId]: newEntryData }));

    const currentData = form.getFieldValue("entryData") || {};
    form.setFieldValue("entryData", {
      ...currentData,
      [entryId]: newEntryData,
    });

    message.info(
      `Đã thêm dòng thu cho: ${customer.name}${assetInfo ? ` - ${assetInfo}` : ""}`,
    );
  };

  /* ================== ĐOẠN CODE CŨ (ĐÃ YÊU CẦU COMMENT LẠI) ==================
  // ✅ addAllEntries (OLD)
  const addAllEntries = () => {
    if (availableCustomers.length === 0) {
      message.warning("Không có khách hàng nào để thêm");
      return;
    }
    if (!selectedAccount) {
      message.warning("Vui lòng chọn tài khoản thanh toán trước");
      return;
    }

    const notAddedCustomers = availableCustomers.filter(
      (customer) => !entries.some((e) => e.customer_id === customer.id),
    );
    if (notAddedCustomers.length === 0) {
      message.warning("Tất cả khách hàng đã được thêm rồi");
      return;
    }

    const newEntries = [];
    const newEntryValuesMap = {};
    const currentData = form.getFieldValue("entryData") || {};
    const newData = { ...currentData };

    notAddedCustomers.forEach((customer) => {
      const entryId = generateEntryId();
      newEntries.push({
        id: entryId,
        customer_id: customer.id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        customer_discount_type: customer.discount_type ?? null,
        customer_discount_value: customer.discount_value ?? 0,
        organization_id: valueOrg ?? null,
        charge_target_number: "",
        asset_info: "",
        assets: customer.assets || [],
      });

      const entryData = {
        discount_override: 0,
        charge_target_number: "",
        acc_payment_id: selectedAccount.id,
      };
      fees.forEach((fee) => {
        entryData[fee.id] = fee.price || 0;
      });
      if (customer.discount_type === "FIXED") {
        entryData.discount_override = Number(customer.discount_value) || 0;
      }

      setDiscountModes((prev) => ({ ...prev, [entryId]: "auto" }));
      newEntryValuesMap[entryId] = entryData;
      newData[entryId] = entryData;
    });

    setEntries((prev) => [...prev, ...newEntries]);
    setEntryValues((prev) => ({ ...prev, ...newEntryValuesMap }));
    form.setFieldValue("entryData", newData);
    message.info(`Đã thêm ${notAddedCustomers.length} dòng thu`);
  };
  ============================================================================= */

  // ✅ addAllEntries: sync cả form và entryValues (NEW TÀI SẢN CHIẾM VỊ TRÍ)
  const addAllEntries = () => {
    if (selectionMode === "CUSTOMER" && availableCustomers.length === 0) {
      message.warning("Không có khách hàng nào để thêm");
      return;
    }
    if (selectionMode === "ASSET" && availableAssets.length === 0) {
      message.warning("Không có tài sản nào để thêm");
      return;
    }
    if (!selectedAccount) {
      message.warning("Vui lòng chọn tài khoản thanh toán trước");
      return;
    }

    const newEntries = [];
    const newEntryValuesMap = {};
    const currentData = form.getFieldValue("entryData") || {};
    const newData = { ...currentData };

    if (selectionMode === "CUSTOMER") {
        const notAddedCustomers = availableCustomers.filter(
          (customer) => !entries.some((e) => Number(e.customer_id) === Number(customer.id)),
        );
        if (notAddedCustomers.length === 0) {
          message.warning("Tất cả khách hàng đã được thêm rồi");
          return;
        }

        notAddedCustomers.forEach((customer) => {
          const entryId = generateEntryId();
          newEntries.push({
            id: entryId,
            customer_id: customer.id,
            customer_name: customer.name,
            customer_phone: customer.phone,
            customer_discount_type: customer.discount_type ?? null,
            customer_discount_value: customer.discount_value ?? 0,
            organization_id: valueOrg ?? null,
            charge_target_number: "",
            asset_info: "",
            assets: customer.assets || [],
          });

          const entryData = {
            discount_override: 0,
            charge_target_number: "",
            acc_payment_id: selectedAccount.id,
          };
          fees.forEach((fee) => {
            entryData[fee.id] = fee.price || 0;
          });
          if (customer.discount_type === "FIXED") {
            entryData.discount_override = Number(customer.discount_value) || 0;
          }

          setDiscountModes((prev) => ({ ...prev, [entryId]: "auto" }));
          newEntryValuesMap[entryId] = entryData;
          newData[entryId] = entryData;
        });
        message.info(`Đã thêm ${notAddedCustomers.length} dòng thu Khách hàng`);

    } else {
        // Chế độ Tài sản
        const notAddedAssets = availableAssets.filter(
          (asset) => !entries.some(
              (e) => Number(e.customer_id) === Number(asset.customer?.id) && 
              (String(e.asset_info) === String(asset.id) || String(e.charge_target_number) === String(asset.id) || String(e.asset_id) === String(asset.id))
          ),
        );
        if (notAddedAssets.length === 0) {
          message.warning("Tất cả tài sản đã được thêm rồi");
          return;
        }

        notAddedAssets.forEach((asset) => {
          const customer = asset.customer;
          if (!customer) return; // Bỏ qua nếu ko có chủ

          const entryId = generateEntryId();
          newEntries.push({
            id: entryId,
            customer_id: customer.id,
            customer_name: customer.name,
            customer_phone: customer.phone,
            customer_discount_type: customer.discount_type ?? null,
            customer_discount_value: customer.discount_value ?? 0,
            organization_id: valueOrg ?? null,
            charge_target_number: asset.id,
            asset_info: asset.id,
            assets: customer.assets || [asset],
          });

          const entryData = {
            discount_override: 0,
            charge_target_number: asset.id,
            acc_payment_id: selectedAccount.id,
          };
          fees.forEach((fee) => {
            entryData[fee.id] = fee.price || 0;
          });
          if (customer.discount_type === "FIXED") {
            entryData.discount_override = Number(customer.discount_value) || 0;
          }

          setDiscountModes((prev) => ({ ...prev, [entryId]: "auto" }));
          newEntryValuesMap[entryId] = entryData;
          newData[entryId] = entryData;
        });
        message.info(`Đã thêm ${notAddedAssets.length} dòng thu Tài sản`);
    }

    setEntries((prev) => [...prev, ...newEntries]);
    setEntryValues((prev) => ({ ...prev, ...newEntryValuesMap }));
    form.setFieldValue("entryData", newData);
  };

  // ✅ removeEntry: xóa khỏi entryValues
  const removeEntry = (entryId) => {
    setEntries((prev) => prev.filter((e) => e.id !== entryId));
    form.setFieldValue(["entryData", entryId], undefined);
    setEntryValues((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
    setDiscountModes((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
    message.info("Đã xóa dòng thu");
  };

  // ✅ copyEntry: dùng entryValues làm source
  const copyEntry = (sourceEntry) => {
    const newEntryId = generateEntryId();
    setEntries((prev) => [
      ...prev,
      {
        ...sourceEntry,
        id: newEntryId,
        db_id: null,
        asset_info: "",
        charge_target_number: "",
        assets: sourceEntry.assets || [],
      },
    ]);

    const sourceData = entryValues[sourceEntry.id] || {};
    const copiedData = { ...sourceData };
    fees.forEach((fee) => {
      copiedData[fee.id] = Number(sourceData[fee.id]) || 0;
    });
    copiedData.charge_target_number = "";
    copiedData.acc_payment_id =
      sourceData.acc_payment_id || selectedAccount?.id || "";
    copiedData.discount_override = Number(sourceData.discount_override) || 0;

    setEntryValues((prev) => ({ ...prev, [newEntryId]: copiedData }));
    form.setFieldValue(["entryData", newEntryId], copiedData);
    setDiscountModes((prev) => ({
      ...prev,
      [newEntryId]: prev[sourceEntry.id] || "auto",
    }));
    message.info(`Đã sao chép dòng thu cho ${sourceEntry.customer_name}`);
  };

  // ── Quản lý khoản thu (Fee management) ──────────────────────────────────────────

  // ✅ addFeeType: sync entryValues
  /**
   * Sinh mã khoản thu mới không đụng mã đang có.
   *
   * Bản cũ lấy `K${fees.length + 1}`: xoá một khoản ở giữa là độ dài mảng tụt xuống và
   * mã sinh ra trùng mã cũ (K1,K2,K3 → xoá K2 → thêm mới ra K3). Hai khoản cùng mã thì
   * dùng chung một ô giá trị, và khi lưu `fees` là object nên một khoản biến mất — tiền
   * gửi lên hụt đúng số tiền của khoản đó mà không có dấu hiệu gì.
   *
   * Tránh cả trùng TÊN vì file Excel lấy tên làm tiêu đề cột.
   */
  const nextFeeId = (currentFees) => {
    const usedIds = new Set(currentFees.map((f) => String(f.id)));
    const usedNames = new Set(
      currentFees.map((f) => String(f.name ?? "").trim().toLowerCase()),
    );

    const maxIndex = currentFees.reduce((max, f) => {
      const matched = /^K(\d+)$/.exec(String(f.id));
      return matched ? Math.max(max, Number(matched[1])) : max;
    }, 0);

    let index = maxIndex + 1;
    while (usedIds.has(`K${index}`) || usedNames.has(`k${index}`)) index++;

    return `K${index}`;
  };

  const addFeeType = () => {
    const newFeeId = nextFeeId(fees);
    setFees((prev) => [
      ...prev,
      { id: newFeeId, name: newFeeId, price: 0, tax: 0, unit: "" },
    ]);

    const currentData = form.getFieldValue("entryData") || {};
    Object.keys(entryValues).forEach((entryId) => {
      currentData[entryId] = { ...(entryValues[entryId] || {}), [newFeeId]: 0 };
    });
    form.setFieldValue("entryData", currentData);

    setEntryValues((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((entryId) => {
        updated[entryId] = { ...updated[entryId], [newFeeId]: 0 };
      });
      return updated;
    });
    message.info(`Đã thêm khoản thu: ${newFeeId}`);
  };

  // ✅ removeFeeType: sync entryValues
  const removeFeeType = (feeId) => {
    if (fees.length <= 1) {
      message.warning("Phải có ít nhất một khoản thu");
      return;
    }
    const feeToRemove = fees.find((f) => f.id === feeId);
    setFees((prev) => prev.filter((f) => f.id !== feeId));

    const newFormData = {};
    Object.keys(entryValues).forEach((entryId) => {
      newFormData[entryId] = { ...(entryValues[entryId] || {}) };
      delete newFormData[entryId][feeId];
    });
    form.setFieldValue("entryData", newFormData);

    setEntryValues((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((entryId) => {
        const copy = { ...updated[entryId] };
        delete copy[feeId];
        updated[entryId] = copy;
      });
      return updated;
    });
    message.info(`Đã xóa khoản thu: ${feeToRemove?.name}`);
  };

  const openEditFeeModal = (fee) => {
    setEditingFee(fee);
    setTempFeeName(fee.name);
    setTempFeePrice(fee.price || 0);
    setTempFeeTax(fee.tax || 0);
    setTempUnit(fee?.unit || "");
    setApplyPriceToAll(false);
    setFeeNameModalVisible(true);
  };

  // ✅ saveFeeNameChange: sync entryValues
  const saveFeeNameChange = () => {
    if (!tempFeeName.trim()) {
      message.error("Tên khoản thu không được để trống");
      return;
    }

    // Tên khoản thu chính là tiêu đề cột trong file Excel. Hai khoản trùng tên thì lúc
    // nhập lại chỉ đọc được một cột, khoản còn lại mất số tiền vừa nhập.
    const nameKey = tempFeeName.trim().toLowerCase();
    const trungTen = fees.some(
      (fee) =>
        fee.id !== editingFee.id &&
        String(fee.name ?? "").trim().toLowerCase() === nameKey,
    );
    if (trungTen) {
      message.error(`Đã có khoản thu tên "${tempFeeName.trim()}". Vui lòng đặt tên khác.`);
      return;
    }

    setFees((prev) =>
      prev.map((fee) =>
        fee.id === editingFee.id
          ? {
              ...fee,
              name: tempFeeName.trim(),
              price: tempFeePrice,
              tax: tempFeeTax,
              unit: tempUnit,
            }
          : fee,
      ),
    );

    if (applyPriceToAll) {
      const newFormData = {};
      Object.keys(entryValues).forEach((entryId) => {
        newFormData[entryId] = {
          ...(entryValues[entryId] || {}),
          [editingFee.id]: tempFeePrice,
        };
      });
      form.setFieldValue("entryData", newFormData);

      setEntryValues((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((entryId) => {
          updated[entryId] = {
            ...updated[entryId],
            [editingFee.id]: tempFeePrice,
          };
        });
        return updated;
      });
      message.info("Đã cập nhật tên và giá khoản thu cho tất cả khách hàng");
    } else {
      message.info("Đã cập nhật tên khoản thu");
    }

    setFeeNameModalVisible(false);
    setEditingFee(null);
    setTempFeeName("");
    setTempUnit("");
    setTempFeeTax(0);
    setTempFeePrice(0);
    setApplyPriceToAll(false);
  };

  const handleAddCustomer = () => {
    Modal.confirm({
      title: "Xác nhận",
      content: "Bạn có chắc chắn muốn chuyển trang tạo khách hàng mới không?",
      okText: "Đồng ý",
      cancelText: "Hủy",
      onOk: () => navigate("/store-customer"),
    });
  };

  // ── Cấu hình cột (Columns) ─────────────────────────────────────────────────────

  const columns = React.useMemo(() => [
    {
      title: "Khách hàng",
      dataIndex: "customer_name",
      key: "customer_name",
      width: 180,
      // Ghim lại khi cuộn ngang: bảng rộng hơn màn hình nhiều, không ghim thì nhập
      // tới cột phí thứ ba là không còn biết đang nhập cho ai.
      fixed: "left",
      render: (text, record) => {
        const customerInfoContent = (
          <div style={{ maxWidth: 320 }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 16 }}>
                {record.customer_name}
              </Text>
            </div>
            <Divider style={{ margin: "8px 0" }} />
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Số điện thoại:
              </Text>
              <div>
                <Text strong>{record.customer_phone}</Text>
              </div>
            </div>
            {record.customer_discount_type ? (
              <>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Loại giảm trừ:
                  </Text>
                  <div>
                    <Text strong>
                      {record.customer_discount_type === "PERCENTAGE"
                        ? "Phần trăm (%)"
                        : "Cố định (đ)"}
                    </Text>
                  </div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Giá trị giảm trừ:
                  </Text>
                  <div>
                    <Text strong style={{ color: "#A8681E" }}>
                      {record.customer_discount_type === "PERCENTAGE"
                        ? `${record.customer_discount_value}%`
                        : `${Number(record.customer_discount_value || 0).toLocaleString("vi-VN")} đ`}
                    </Text>
                  </div>
                </div>
              </>
            ) : (
              <Alert
                message="Không có giảm trừ"
                type="info"
                showIcon
                style={{ fontSize: 12, padding: "4px 8px" }}
              />
            )}
            <Divider style={{ margin: "8px 0" }} />
            <Button
              type="link"
              size="small"
              icon={<UserOutlined />}
              style={{ padding: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                Modal.confirm({
                  title: "Xem chi tiết khách hàng",
                  icon: <ExclamationCircleOutlined />,
                  content: `Bạn có muốn chuyển đến trang chi tiết của khách hàng "${record.customer_name}" không?`,
                  okText: "Xem chi tiết",
                  cancelText: "Hủy",
                  onOk: () =>
                    navigate(`/customers/information/${record.customer_id}`),
                });
              }}
            >
              Xem chi tiết khách hàng
            </Button>
          </div>
        );

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
            }}
          >
            <div style={{ flex: 1 }}>
              <div className="font-medium">{text}</div>
              <Text type="secondary" className="text-xs">
                {record.customer_phone}
              </Text>
            </div>
            <Popover
              content={customerInfoContent}
              title={null}
              trigger="hover"
              placement="rightTop"
              overlayStyle={{ maxWidth: 350 }}
            >
              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined />}
                style={{ color: "#0070D7", padding: 4, height: "auto" }}
              />
            </Popover>
          </div>
        );
      },
    },
    ...(!sortByOrg
      ? [
          {
            title: "Đơn vị",
            dataIndex: "org_id",
            key: "org_id",
            width: 180,
            render: (_, record) => {
              const orgItem = treeData.find(
                (c) => Number(c.id) === Number(record.organization_id),
              );
              if (!orgItem) return <Text type="secondary">Không xác định</Text>;
              return (
                <Popover
                  content={
                    <div>
                      <div>
                        <Text strong>ID:</Text> {orgItem.id}
                      </div>
                      <div>
                        <Text strong>Tên:</Text> {orgItem.title}
                      </div>
                    </div>
                  }
                  title="Thông tin đơn vị"
                  trigger="hover"
                >
                  <Text style={{ cursor: "pointer" }}>{orgItem.title}</Text>
                </Popover>
              );
            },
          },
        ]
      : []),
    ...(collectionType !== "CUSTOMER"
      ? [
          {
            title: "Tài sản",
            key: "charge_target_number",
            width: 210,
            align: "center",
            render: (_, record) => {
              const customerInAvailable = availableCustomers.find(
                (c) => Number(c.id) === Number(record.customer_id),
              );
              const customerAssets =
                customerInAvailable?.assets?.length > 0
                  ? customerInAvailable.assets
                  : record.assets || [];
              const hasAssets = customerAssets.length > 0;

              return (
                <Form.Item
                  name={["entryData", record.id, "charge_target_number"]}
                  style={{ margin: 0, width: "100%" }}
                  preserve={true}
                >
                  {hasAssets ? (
                    <Select
                      placeholder="Chọn tài sản"
                      className="w-full"
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={customerAssets.map((asset) => ({
                        value: asset.id,
                        label: `${asset.name || ""}`.trim(),
                      }))}
                      onChange={(val) =>
                        updateEntryValue(
                          record.id,
                          "charge_target_number",
                          val ?? "",
                        )
                      }
                    />
                  ) : (
                    <Input
                      placeholder="Nhập tài sản"
                      className="w-full"
                      onBlur={(e) =>
                        updateEntryValue(
                          record.id,
                          "charge_target_number",
                          e.target.value ?? "",
                        )
                      }
                    />
                  )}
                </Form.Item>
              );
            },
          },
        ]
      : []),
    // ✅ Fee columns dùng FeeInputCell + entryValues
    ...fees.map((fee) => ({
      title: (
        <Space direction="vertical" size={0}>
          <Space>
            <span
              className="cursor-pointer hover:text-blue-600 hover:underline"
              onClick={() => openEditFeeModal(fee)}
              title="Click để đổi tên"
            >
              {fee.name}
            </span>
            <Popconfirm
              title="Xóa khoản thu này?"
              description="Dữ liệu của khoản thu sẽ bị xóa"
              onConfirm={() => removeFeeType(fee.id)}
              okText="Xóa"
              cancelText="Hủy"
              disabled={fees.length <= 1}
            >
              <Button
                size="small"
                type="text"
                icon={<DeleteOutlined />}
                danger
                disabled={fees.length <= 1}
                className="opacity-60 hover:opacity-100"
              />
            </Popconfirm>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Thuế {fee?.tax ?? 0}%{fee?.unit ? ` · ${fee.unit}` : ""}
          </Text>
        </Space>
      ),
      dataIndex: fee.id,
      key: fee.id,
      width: 180,
      render: (_, record) => {
        const val = entryValues[record.id]?.[fee.id] ?? 0;
        return (
          <FeeInputCell
            initialValue={val}
            disabled={false}
            onCommit={(v) => {
              updateEntryValue(record.id, fee.id, v);
              form.setFieldValue(["entryData", record.id, fee.id], v);
            }}
          />
        );
      },
    })),
    // ✅ Discount column dùng DiscountCell
    {
      title: "Giảm trừ",
      key: "discount_override",
      width: 150,
      align: "right",
      render: (_, record) => (
        <DiscountCell
          record={record}
          fees={fees}
          entryValues={entryValues}
          discountMode={discountModes[record.id]}
          isEditable={true}
          onCommit={(v) => handleDiscountCommit(record.id, v)}
          onModeChange={(newMode) => handleModeChange(record.id, newMode)}
        />
      ),
    },
    // ✅ Total column dùng entryValues
    {
      title: "Tổng cộng",
      key: "total",
      width: 190,
      align: "right",
      render: (_, record) => {
        const eData = entryValues[record.id] || {};
        const subtotal = fees.reduce(
          (sum, fee) => sum + (Number(eData[fee.id]) || 0),
          0,
        );
        const discountValue = Number(eData.discount_override) || 0;
        const total = Math.max(0, subtotal - discountValue);
        return (
          <Text strong className="text-blue-600">
            {total.toLocaleString("vi-VN")} đ
          </Text>
        );
      },
    },
    {
      title: "Tài khoản",
      dataIndex: "acc_payment_id",
      key: "acc_payment_id",
      width: 260,
      render: (_, record) => (
        <Form.Item
          name={["entryData", record.id, "acc_payment_id"]}
          style={{ margin: 0, width: "100%" }}
          rules={[{ required: true, message: "Chọn tài khoản" }]}
          preserve={true}
        >
          <Select
            style={{ width: "100%" }}
            placeholder="Chọn tài khoản"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={accounts.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            onChange={(val) =>
              updateEntryValue(record.id, "acc_payment_id", val ?? "")
            }
          />
        </Form.Item>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<CopyOutlined />}
            size="small"
            onClick={() => copyEntry(record)}
            title="Sao chép dòng"
          />
          <Popconfirm
            title="Xóa dòng thu?"
            description="Xóa dòng thu khỏi đợt thu"
            onConfirm={() => removeEntry(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ], [
    fees,
    entryValues,
    discountModes,
    accounts,
    treeData,
    sortByOrg,
    collectionType,
    availableCustomers,
    updateEntryValue,
    handleDiscountCommit,
    handleModeChange,
  ]);

  // ── Nguồn dữ liệu bảng (Table data source) ─────────────────────────────────────

  const { zeroEntries, zeroFilterIds, showZero, clearZero, toggleZero } =
    useZeroFeeFilter(entries, entryValues, fees);

  const tableDataSource = React.useMemo(() => {
    let data = entries.map((entry) => ({ key: entry.id, ...entry }));

    if (zeroFilterIds) {
      data = data.filter((item) => zeroFilterIds.has(item.id));
    }

    if (filterOrgId) {
      data = data.filter(
        (item) => Number(item.organization_id) === Number(filterOrgId),
      );
    }
    if (filterText.trim()) {
      const searchText = filterText.toLowerCase().trim();

      // Phải ép String trước khi hạ chữ thường: `asset_info` giữ id tài sản, mà id là SỐ
      // với mọi dòng dựng trong phiên (nhập Excel, nút thêm tài sản). Optional chaining
      // chỉ chặn null/undefined nên `(3545)?.toLowerCase()` vẫn ném TypeError giữa lúc
      // render, và app không có ErrorBoundary nên cả cây bị gỡ -> trắng màn hình.
      const match = (value) => String(value ?? "").toLowerCase().includes(searchText);

      data = data.filter(
        (item) =>
          match(item.customer_name) ||
          match(item.customer_phone) ||
          match(item.asset_info),
      );
    }

    if (sortByOrg) {
      const grouped = data.reduce((acc, entry) => {
        const orgId = entry.organization_id || "unknown";
        if (!acc[orgId]) acc[orgId] = [];
        acc[orgId].push(entry);
        return acc;
      }, {});

      const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
        const orgA = treeData.find((c) => Number(c.id) === Number(a));
        const orgB = treeData.find((c) => Number(c.id) === Number(b));
        // numeric: true để "Khu phố 8" đứng trước "Khu phố 16"
        return (orgA?.title || "Không xác định").localeCompare(
          orgB?.title || "Không xác định",
          "vi",
          { numeric: true },
        );
      });

      const result = [];
      sortedGroups.forEach(([orgId, items]) => {
        const orgItem = treeData.find((c) => Number(c.id) === Number(orgId));
        result.push({
          key: `header_${orgId}`,
          isHeader: true,
          orgName: orgItem?.title || "Không xác định",
          itemCount: items.length,
        });
        result.push(...items);
      });
      return result;
    }
    return data;
  }, [entries, filterOrgId, filterText, sortByOrg, treeData, zeroFilterIds]);

  const uniqueOrgs = React.useMemo(() => {
    const orgIds = [
      ...new Set(entries.map((e) => e.organization_id).filter(Boolean)),
    ];
    return orgIds
      .map((id) => {
        const org = treeData.find((c) => Number(c.id) === Number(id));
        return { id, name: org?.title || "Không xác định" };
      })
      .sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true }));
  }, [entries, treeData]);

  const visibleEntryCount = tableDataSource.filter((item) => !item.isHeader).length;

  // Kéo hàng tiêu đề chạy ngang theo thân bảng. Gắn thẳng listener trên DOM thay vì
  // dùng onScroll của danh sách ảo: onScroll ở đó chỉ báo vị trí cuộn dọc.
  React.useEffect(() => {
    const body = tableBodyRef.current;
    const header = tableHeaderRef.current;
    if (!body || !header) return;

    const syncHeader = () => {
      header.scrollLeft = body.scrollLeft;
    };

    body.addEventListener("scroll", syncHeader, { passive: true });
    syncHeader();

    return () => body.removeEventListener("scroll", syncHeader);
  }, [tableDataSource.length, currentStep]);

  // Bảng cao theo cửa sổ. Chỉ đặt lại state khi con số thật sự đổi để kéo giãn cửa sổ
  // không bắt cả bảng vẽ lại liên tục.
  React.useEffect(() => {
    const onResize = () =>
      setTableMaxHeight((prev) => {
        const next = computeTableHeight();
        return next === prev ? prev : next;
      });

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Mô tả ngắn của từng bước, hiện dưới tiêu đề trang. Bản cũ còn title + focus cho
  // khối hero riêng — khối đó đã bỏ vì lặp lại đúng nội dung Steps và ô bắt buộc.
  const stepGuides = [
    { description: "Nhập đủ thông tin cơ bản để tạo đúng kỳ thu ngay từ lần đầu." },
    { description: "Thêm khách hàng hoặc tài sản, sau đó kiểm tra tài khoản thanh toán." },
    { description: "Đối chiếu lại thông tin tổng hợp trước khi bấm lưu đợt thu." },
  ];
  const currentStepGuide = stepGuides[currentStep] || stepGuides[0];
  const getCollectionSummary = () =>
    entries.reduce(
      (totals, entry) => {
        const eData = entryValues[entry.id] || {};
        const subtotal = fees.reduce(
          (sum, fee) => sum + (Number(eData[fee.id]) || 0),
          0,
        );
        const discount = Number(eData.discount_override) || 0;

        totals.grandSubtotal += subtotal;
        totals.grandDiscount += discount;
        totals.grandTotal += Math.max(0, subtotal - discount);

        return totals;
      },
      { grandSubtotal: 0, grandDiscount: 0, grandTotal: 0 },
    );

  // ── Gửi dữ liệu (Submit) ───────────────────────────────────────────────────────

  // ✅ handleSubmit: đọc từ entryValues
  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      if (!values.name?.trim()) {
        message.error("Vui lòng nhập tên đợt thu");
        return;
      }
      if (!values.dateRange || values.dateRange.length !== 2) {
        message.error("Vui lòng chọn thời gian bắt đầu và kết thúc");
        return;
      }
      if (entries.length === 0) {
        message.error("Vui lòng thêm ít nhất một khách hàng");
        return;
      }

      const zeroList = findZeroEntries(entries, entryValues, fees);
      if (zeroList.length > 0 && !(await confirmZeroEntries(zeroList.length))) {
        // Về bước nhập liệu, bỏ các lọc khác để thấy đủ dòng 0đ
        setFilterText("");
        setFilterOrgId(null);
        showZero(zeroList);
        setCurrentStep(1);
        return;
      }

      const customer_fees = entries.map((entry) => {
        const eData = { ...(entryValues[entry.id] || {}) };
        const accPaymentId =
          eData.acc_payment_id || selectedAccount?.id || null;
        if (!accPaymentId) {
          throw new Error(
            `Vui lòng chọn tài khoản thanh toán cho khách hàng: ${entry.customer_name}`,
          );
        }

        const assetVal = eData.charge_target_number;
        const selectedAsset = entry.assets?.find((a) => a.id === assetVal);

        return {
          id: entry.db_id || null,
          customer_id: entry.customer_id,
          asset_id: selectedAsset ? selectedAsset.id : null,
          fees: fees.reduce((feeObj, fee) => {
            feeObj[fee.id] = Number(eData[fee.id]) || 0;
            return feeObj;
          }, {}),
          discount_override: Number(eData.discount_override) || 0,
          organization_id: Number(entry.organization_id) || null,
          charge_target_number: selectedAsset ? selectedAsset.name : assetVal,
          acc_payment_id: accPaymentId,
        };
      });

      const collectionData = collectionHelpers.formatCollectionData({
        name: values.name.trim(),
        description: values.description?.trim() || "",
        start_date: values.dateRange[0].format("YYYY-MM-DD"),
        end_date: values.dateRange[1].format("YYYY-MM-DD"),
        status: values.status || "PLANNED",
        customer_fees,
        fee_definitions: fees.map((fee) => ({
          id: fee.id,
          name: fee.name,
          tax: fee.tax,
          unit: fee.unit,
        })),
        is_send_zns: values.is_send_zns ?? false,
        send_schedule_at: valueSendScheduleAt
          ? dayjs(valueSendScheduleAt).format("HH:mm:ss")
          : null,
        send_date: values.send_date
          ? values.send_date.format("YYYY-MM-DD")
          : null,
        before_date: values.before_date ?? null,
        after_date: values.after_date ?? null,
        charge_target_type: values.collection_type ?? "APARTMENT",
        collection_category_id: values.collection_category_id ?? null,
        is_locked: values.is_locked ?? true,
        transfer_content: values.transfer_content?.trim() || null,
      });

      const validation =
        collectionHelpers.validateCollectionData(collectionData);
      if (!validation.isValid) {
        message.error(
          "Dữ liệu không hợp lệ: " + Object.values(validation.errors)[0],
        );
        return;
      }

      await collectionAPIWithErrorHandling.createCollection(collectionData);
      message.success("Tạo đợt thu thành công!");
      navigate("/collections");
    } catch (error) {
      message.error("Lỗi khi tạo đợt thu: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ handleTemplateSelect: dùng entryValues
  const handleTemplateSelect = (templateData) => {
    if (!templateData?.items?.length) {
      message.warning("Mẫu không có khoản thu nào");
      setTemplateModalVisible(false);
      return;
    }
    Modal.confirm({
      title: "Xác nhận áp dụng mẫu",
      content: `Bạn có chắc chắn muốn áp dụng mẫu "${templateData.name}" với ${templateData.items.length} khoản thu cho ${entries.length} khách hàng? Dữ liệu hiện tại sẽ bị ghi đè.`,
      okText: "Áp dụng",
      cancelText: "Hủy",
      onOk: () => {
        const newFees = templateData.items.map((item, index) => ({
          id: `K${index + 1}`,
          name: item.item_name,
          price: Number(item.unit_price) || 0,
          tax: Number(item.tax) || 0,
          unit: item.unit_name || "",
        }));
        setFees(newFees);

        const newData = {};
        entries.forEach((entry) => {
          const entryId = entry.id;
          newData[entryId] = {
            discount_override: entryValues[entryId]?.discount_override || 0,
            charge_target_number:
              entryValues[entryId]?.charge_target_number || "",
            acc_payment_id: entryValues[entryId]?.acc_payment_id,
          };
          newFees.forEach((fee) => {
            newData[entryId][fee.id] = fee.price;
          });
        });

        form.setFieldValue("entryData", newData);
        setEntryValues((prev) => {
          const updated = { ...prev };
          entries.forEach((entry) => {
            updated[entry.id] = {
              ...(updated[entry.id] || {}),
              ...(newData[entry.id] || {}),
            };
          });
          return updated;
        });
        message.info(`Đã áp dụng mẫu "${templateData.name}"`);
        setTemplateModalVisible(false);
      },
    });
  };

  const handleReset = () => {
    form.resetFields();
    setEntries([]);
    setEntryValues({});
    setDiscountModes({});
    setFees([
      { id: "K1", name: "K1", tax: 0, unit: "" },
      { id: "K2", name: "K2", tax: 0, unit: "" },
      { id: "K3", name: "K3", tax: 0, unit: "" },
    ]);
    message.info("Đã xóa tất cả dữ liệu nhập");
  };

  const handleBack = () => {
    navigate("/collections");
    message.info("Quay lại danh sách đợt thu");
  };

  const disabledDate = (current) => current && current < dayjs().endOf("day");

  // ── Fee Name Edit Modal ───────────────────────────────────────────────────────

  const FeeNameEditModal = (
    <Modal
      title="Cấu hình khoản thu"
      width="40%"
      open={feeNameModalVisible}
      onOk={saveFeeNameChange}
      onCancel={() => {
        setFeeNameModalVisible(false);
        setEditingFee(null);
        setTempFeeName("");
        setTempFeePrice(0);
        setApplyPriceToAll(false);
      }}
      okText="Lưu"
      cancelText="Hủy"
      className="collection-fee-modal"
    >
      <Form.Item label="Tên khoản thu" required>
        <Input
          value={tempFeeName}
          onChange={(e) => setTempFeeName(e.target.value)}
          placeholder="Nhập tên khoản thu mới"
          maxLength={50}
          showCount
        />
      </Form.Item>
      <Form.Item
        label="Giá mặc định"
        required
        tooltip="Giá này sẽ được áp dụng cho khách hàng mới thêm vào"
      >
        <InputNumber
          value={tempFeePrice}
          onChange={(value) => setTempFeePrice(value || 0)}
          min={0}
          max={999999999}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
          placeholder="0"
          style={{ width: "100%" }}
          addonAfter="đ"
        />
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col lg={12} sm={24}>
          <Form.Item label="Thuế (%)" tooltip="Thuế đã bao gồm trong giá">
            <InputNumber
              value={tempFeeTax}
              onChange={(value) => setTempFeeTax(value || 0)}
              min={0}
              max={100}
              formatter={(value) => `${value}`}
              placeholder="0"
              style={{ width: "100%" }}
              addonAfter="%"
            />
          </Form.Item>
        </Col>
        <Col lg={12} sm={24}>
          <Form.Item label="Đơn vị" tooltip="Đơn vị">
            <Input
              value={tempUnit}
              onChange={(e) => setTempUnit(e.target.value ?? "")}
              placeholder="Đơn vị khoản thu"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <Space direction="horizontal" style={{ width: "100%" }}>
          <Switch
            checked={applyPriceToAll}
            onChange={(checked) => setApplyPriceToAll(checked)}
          />
          <Text>Áp dụng giá này cho tất cả khách hàng hiện tại</Text>
        </Space>
      </Form.Item>
      {applyPriceToAll && (
        <Alert
          message="Bạn đang thay đổi giá cho TẤT CẢ khách hàng hiện tại. Hành động này sẽ ghi đè lên giá trị cũ."
          type="warning"
          icon={<ExclamationCircleOutlined />}
          style={{ fontSize: 12 }}
        />
      )}
    </Modal>
  );

  // ── Hiển thị (Render) ──────────────────────────────────────────────────────────

  return (
    <div className="collection-form-page">
      <div className="collection-page-header">
        <div className="collection-page-header-main">
        <Breadcrumb
          separator=">"
          items={[
            {
              href: "/",
              title: (
                <>
                  <HomeOutlined /> Trang chủ
                </>
              ),
            },
            {
              href: "/collections",
              title: (
                <>
                  <DollarOutlined /> Đợt thu
                </>
              ),
            },
            {
              title: (
                <>
                  <PlusOutlined /> Thêm mới
                </>
              ),
            },
          ]}
        />
          <h2>Tạo đợt thu mới</h2>
          <p>{currentStepGuide.description}</p>
        </div>

        {/* Steps nằm chung dải với tiêu đề. Bản cũ còn một khối hero phía trên
            lặp lại đúng những gì Steps đã nói, tốn hơn 100px ở mọi bước. */}
        <Steps
          className="collection-stepper"
          current={currentStep}
          onChange={async (step) => {
            if (submitting) return;
            if (currentStep === 0 && step > 0) {
              try {
                await form.validateFields(['name', 'dateRange', 'collection_type', 'status']);
                setCurrentStep(step);
              } catch (_) {}
            } else {
              setCurrentStep(step);
            }
          }}
          size="small"
          items={[
            { title: 'Thông tin' },
            { title: 'Dòng thu' },
            { title: 'Xác nhận' },
          ]}
        />
      </div>

      <Spin spinning={submitting} tip="Đang lưu thông tin...">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          preserve={true}
          initialValues={{
            status: "PLANNED",
            is_send_zns: true,
            collection_type: "APARTMENT",
            is_locked: true,
          }}
        >

          {/* ── Bước 1: Thông tin đợt thu ── */}
          <div style={{ display: currentStep !== 0 ? 'none' : 'block' }}>
          {/* ── Thông tin + Cấu hình gửi tin ── */}
          <Row gutter={[24, 16]}>
            <Col xs={24} lg={17} md={14}>
              <Card
                title={
                  <span>
                    <span className="collection-card-icon blue">
                      <CalendarOutlined />
                    </span>
                    Thông tin đợt thu
                  </span>
                }
                className="collection-section-card collection-card-blue h-full"
              >
                <Row gutter={[12, 0]}>
                  {/* ── Nhóm 1: Thông tin cơ bản ── */}
                  <Col xs={24} md={24}>
                    <Form.Item
                      name="name"
                      label={<span className="font-medium">Tên đợt thu</span>}
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng nhập tên đợt thu",
                        },
                        {
                          max: 200,
                          message: "Tên không được vượt quá 200 ký tự",
                        },
                      ]}
                    >
                      <Input
                        placeholder="VD: Đợt thu tháng 11/2025"
                        className="rounded-lg"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="collection_category_id"
                      label={<span className="font-medium">Loại phí</span>}
                    >
                      <Select
                        placeholder="Chọn loại phí"
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        options={categories.map(cat => ({
                          value: cat.id,
                          label: cat.name
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="collection_type"
                      label="Nhóm đối tượng"
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng chọn nhóm đối tượng",
                        },
                      ]}
                    >
                      <Radio.Group>
                        {collectionTypes.map((item) => (
                          <Radio key={item.value} value={item.value} disabled={item.disabled}>
                            {item.label}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="status"
                      label={<span className="font-medium">Trạng thái</span>}
                      rules={[
                        { required: true, message: "Vui lòng chọn trạng thái" },
                      ]}
                    >
                      <Select
                        placeholder="Chọn trạng thái"
                        options={[
                          { label: "Nháp", value: "PLANNED" },
                          { label: "Đang hoạt động", value: "ONGOING" },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  {/* ── Nhóm 2: Thời gian ── */}
                  <Col xs={24}>
                    <Divider orientation="left" plain style={{ fontSize: 12, color: '#64748B', margin: '4px 0 12px' }}>
                      Thời gian thu
                    </Divider>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="dateRange"
                      label={<span className="font-medium">Khoảng thời gian</span>}
                      rules={[
                        {
                          required: true,
                          message: "Vui lòng chọn thời gian thu",
                        },
                      ]}
                    >
                      <RangePicker
                        className="w-full rounded-lg"
                        format="DD/MM/YYYY"
                        placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
                        onChange={handleDateRangeChange}
                        style={{ width: "100%" }}
                        disabledDate={disabledDate}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="send_date"
                      label={
                        <span className="font-medium">
                          Hạn thu (tin nhắn nhận)
                        </span>
                      }
                    >
                      <DatePicker
                        className="w-full rounded-lg"
                        format="DD/MM/YYYY"
                        placeholder="Chọn ngày"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>

                  {/* Nội dung chuyển khoản và Mô tả phần lớn đợt thu để trống, gom lại
                      cho bước 1 gọn trong một màn. */}
                  <Col xs={24}>
                    <Collapse
                      size="small"
                      className="collection-extra-collapse"
                      items={[{
                        key: "extra",
                        label: (
                          <span className="collection-extra-label">
                            Nội dung chuyển khoản &amp; Mô tả đợt thu
                            <Tag style={{ marginInlineEnd: 0 }}>Tùy chọn</Tag>
                          </span>
                        ),
                        children: (
                          <>
                            <Form.Item
                              name="transfer_content"
                              label={<span className="font-medium">Nội dung chuyển khoản</span>}
                              tooltip="Nội dung hiển thị khi khách hàng quét mã chuyển khoản (Tối đa 30 ký tự)"
                            >
                              <TransferContentInput />
                            </Form.Item>

                            <Form.Item
                              name="description"
                              label={<span className="font-medium">Mô tả đợt thu</span>}
                              style={{ marginBottom: 0 }}
                            >
                              <TextArea
                                placeholder="Mô tả chi tiết về đợt thu này..."
                                rows={3}
                                className="rounded-lg"
                                maxLength={1000}
                                showCount
                              />
                            </Form.Item>
                          </>
                        ),
                      }]}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} lg={7} md={10}>
              <Card
                title={
                  <span>
                    <span className="collection-card-icon green">
                      <EnvironmentOutlined />
                    </span>
                    Cấu hình gửi tin
                  </span>
                }
                className="collection-section-card collection-card-green h-full"
              >
                {/* Switch chính - luôn active */}
                <Form.Item
                  name="is_send_zns"
                  label={<span className="font-medium">Tin thông báo ZNS</span>}
                  valuePropName="checked"
                  style={{ marginBottom: 16 }}
                >
                  <Switch
                    checkedChildren="Bật"
                    unCheckedChildren="Tắt"
                    defaultChecked
                  />
                </Form.Item>

                { (userType === "SHOP" || userType === "WARD_LEADER") && (
                  <>
                    <Form.Item
                      name="is_locked"
                      label={<span className="font-medium">Khóa chỉnh sửa cấp con</span>}
                      valuePropName="checked"
                      style={{ marginBottom: 12 }}
                      extra="Bật để chỉ tài khoản cha được sửa. Tắt để cho phép cấp con chỉnh sửa đợt thu của đơn vị mình."
                    >
                      <Switch checkedChildren="Khóa" unCheckedChildren="Cho sửa" />
                    </Form.Item>

                    <div
                      style={{
                        background: isChildEditLocked ? "#FFF5E8" : "#EAF9F2",
                        border: `1px solid ${isChildEditLocked ? "#FFE0B8" : "#B8E8D0"}`,
                        borderRadius: 8,
                        padding: "10px 12px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: isChildEditLocked ? "#A8681E" : "#128555",
                          marginBottom: 4,
                        }}
                      >
                        {isChildEditLocked
                          ? "Cấp con đang bị khóa chỉnh sửa"
                          : "Cấp con được phép chỉnh sửa"}
                      </div>
                      <div style={{ fontSize: 12, color: "#475569" }}>
                        {isChildEditLocked
                          ? "Đợt thu này chỉ tài khoản cha xử lý cập nhật."
                          : "Nhân sự đơn vị con có thể vào màn chỉnh sửa đợt thu của đơn vị mình."}
                      </div>
                    </div>
                  </>
                )}

                {/* Cấu hình nâng cao - disabled, sớm ra mắt */}
                <div style={{
                  background: '#F8FAFC',
                  border: '1px dashed #CBD5E1',
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 14
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>Cấu hình lịch gửi</span>
                    <span style={{
                      fontSize: 11,
                      background: '#FFF5E8',
                      color: '#A8681E',
                      border: '1px solid #FFE0B8',
                      borderRadius: 4,
                      padding: '1px 6px'
                    }}>⏰ Sắp ra mắt</span>
                  </div>
                  <Row gutter={[8, 8]}>
                    <Col xs={24}>
                      <Form.Item
                        name="send_schedule_at"
                        label={<span style={{ fontSize: 12 }}>Giờ gửi</span>}
                        style={{ marginBottom: 8 }}
                      >
                        <TimePicker
                          style={{ width: "100%" }}
                          placeholder="Giờ gửi"
                          onChange={onChangeSendScheduleAt}
                          size="small"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item
                        name="before_date"
                        label={<span style={{ fontSize: 12 }}>Trước hạn</span>}
                        rules={[{ type: "number", min: 0, message: "≥ 0" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          min={0}
                          placeholder="0"
                          style={{ width: "100%" }}
                          addonAfter="ngày"
                          size="small"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item
                        name="after_date"
                        label={<span style={{ fontSize: 12 }}>Sau hạn</span>}
                        rules={[{ type: "number", min: 1, message: "≥ 1" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          min={1}
                          placeholder="0"
                          style={{ width: "100%" }}
                          addonAfter="ngày"
                          size="small"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

	                <Alert
	                  message="Tin nhắn từ 22:00–06:00 sẽ được gửi vào sáng hôm sau"
	                  type="info"
	                  showIcon
	                  icon={<ExclamationCircleOutlined />}
	                  style={{ fontSize: 12 }}
	                />
	              </Card>

	            </Col>
	          </Row>
          </div>{/* /Bước 1 */}

          {/* ── Bước 2: Thêm dòng thu ── */}
          <div style={{ display: currentStep !== 1 ? 'none' : 'block' }}>
          {/* ── Thêm dòng thu + Tài khoản ── */}
          {/* Hai khối này chỉ dùng lúc thêm dòng. Có dòng rồi thì thu lại thành một
              thanh mỏng để nhường chỗ cho bảng khoản thu — nhãn vẫn nói rõ tài khoản
              đang chọn, hoặc báo đỏ nếu chưa chọn. */}
          <Collapse
            ghost
            size="small"
            className="collection-step2-collapse"
            defaultActiveKey={entries.length === 0 ? ["add"] : []}
            items={[{
              key: "add",
              label: (
                <span className="collection-step2-label">
                  Thêm dòng thu
                  <span className="sep">·</span>
                  {selectedAccount ? (
                    <span>Tài khoản: <strong>{selectedAccount.name}</strong></span>
                  ) : (
                    <Text type="danger" style={{ fontSize: 13 }}>Chưa chọn tài khoản thanh toán</Text>
                  )}
                </span>
              ),
              children: (
          <Row gutter={[24, 16]} style={{ marginTop: 0 }}>
            <Col xs={24} sm={24} md={12} lg={12} xl={16}>
              <Card
                className="collection-section-card collection-card-purple"
                title={
                  <span>
                    <span className="collection-card-icon purple">
                      <UserOutlined />
                    </span>
                    Thêm dòng thu khách hàng
                  </span>
                }
              >
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                      <TreeSelect
                        treeDataSimpleMode
                        style={{ width: "100%" }}
                        value={valueOrg}
                        styles={{
                          popup: { root: { maxHeight: 400, overflow: "auto" } },
                        }}
                        placeholder="Chọn đơn vị"
                        onChange={onChange}
                        treeData={treeData}
                        showSearch
                        filterTreeNode={(input, item) =>
                          item.title.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        treeDefaultExpandAll={false}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Lọc theo nhóm"
                        value={selectedGroup}
                        onChange={handleGroupChange}
                        disabled={
                          !valueOrg ||
                          availableGroups.length === 0 ||
                          loadingGroup
                        }
                        loading={loadingGroup}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        options={availableGroups.map((group) => ({
                          value: group.id,
                          label: group.name,
                        }))}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        {ENABLE_ASSET_MODE && (
                            <div style={{ marginBottom: 12 }}>
                                <Radio.Group value={selectionMode} onChange={handleModeChangeSelection} buttonStyle="solid" size="small">
                                    <Radio.Button value="CUSTOMER">Theo Khách hàng</Radio.Button>
                                    <Radio.Button value="ASSET">Theo Tài sản</Radio.Button>
                                </Radio.Group>
                            </div>
                        )}
                        {selectionMode === "CUSTOMER" ? (
                            <Select
                                placeholder="Chọn khách hàng"
                                style={{ width: "100%" }}
                                showSearch
                                filterOption={(input, option) =>
                                (option?.label ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                loading={loadingCus}
                                disabled={loadingCus || !valueOrg}
                                onSelect={(customerId) => addEntry(customerId, "")}
                                value={undefined}
                                options={availableCustomers.map((customer) => ({
                                  value: customer.id,
                                  label: `${customer.name} - ${customer.phone}`,
                                }))}
                            />
                        ) : (
                            <Select
                                placeholder="Chọn tài sản (Phòng/Vị trí)"
                                style={{ width: "100%" }}
                                showSearch
                                filterOption={(input, option) =>
                                (option?.label ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                                loading={loadingAssets}
                                disabled={loadingAssets || !valueOrg}
                                onSelect={handleAssetSelect}
                                value={undefined}
                                options={availableAssets.map((asset) => ({
                                    value: asset.id,
                                    label: `${asset.name} - Chủ: ${asset.customer?.name || "N/A"}`,
                                }))}
                            />
                        )}
                    </Col>
                  </Row>
                  <Row gutter={[12, 12]}>
                    {/* ── Nhóm hành động chính ── */}
                    <Col xs={24}>
                      <Divider orientation="left" plain style={{ fontSize: 12, color: '#64748B', margin: '0 0 8px' }}>
                        Thêm nhanh
                      </Divider>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={addAllEntries}
                        loading={selectionMode === "ASSET" && loadingAssets}
                        disabled={
                          // Chế độ tài sản: bấm khi danh sách chưa về là "Không có tài sản"
                          // hoặc vớ danh sách của đơn vị trước, cả khối bị bỏ sót
                          selectionMode === "ASSET"
                            ? loadingAssets || availableAssets.length === 0
                            : availableCustomers.length === 0 ||
                              availableCustomers.every((c) =>
                                entries.some((e) => e.customer_id === c.id),
                              )
                        }
                        block
                      >
                        Thêm tất cả
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddCustomer}
                        block
                      >
                        Khách hàng mới
                      </Button>
                    </Col>

                    {/* ── Nhóm công cụ ── */}
                    <Col xs={24}>
                      <Divider orientation="left" plain style={{ fontSize: 12, color: '#64748B', margin: '4px 0 8px' }}>
                        Công cụ
                      </Divider>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={8}>
                      <Button
                        type="dashed"
                        icon={<FileSyncOutlined />}
                        onClick={handleExportTemplate}
                        block
                      >
                        Xuất mẫu Excel
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={8}>
                      <Upload
                        fileList={importFileList}
                        beforeUpload={(file) => {
                          handleImportExcel(file);
                          return false; // Prevent auto-upload
                        }}
                        onChange={({ fileList }) => setImportFileList(fileList)}
                        showUploadList={false}
                        accept=".xlsx,.xls"
                        style={{ width: '100%' }}
                      >
                        <Button
                          type="dashed"
                          icon={<FileSyncOutlined />}
                          block
                          style={{ width: '100%' }}
                        >
                          Nhập từ Excel
                        </Button>
                      </Upload>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={8}>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={addFeeType}
                        disabled={entries.length === 0}
                        block
                      >
                        Thêm khoản thu
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={8}>
                      <Button
                        type="dashed"
                        icon={<FileSyncOutlined />}
                        onClick={() => setTemplateModalVisible(true)}
                        disabled={entries.length === 0}
                        block
                      >
                        Chọn mẫu thu
                      </Button>
                    </Col>
                  </Row>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={8}>
              <Card
                title={
                  <span>
                    <span className="collection-card-icon" style={{ background: '#F0F6FD', marginRight: 8 }}>💳</span>
                    Tài khoản thanh toán
                  </span>
                }
                className={`collection-section-card collection-payment-card ${!selectedAccount ? "collection-payment-card-warning" : ""}`}
              >
                {!selectedAccount && (
                  <Alert
                    message="Vui lòng chọn tài khoản thanh toán trước khi thêm khách hàng"
                    type="warning"
                    showIcon
                    style={{ fontSize: 12, marginBottom: 14 }}
                  />
                )}
	                <PaymentAccountSelector
	                  accounts={accounts}
	                  onSelect={handleSelectAccount}
	                  selectedAccountId={selectedAccount?.id}
	                />

	                {entries.length > 0 && (
	                  <div style={{ marginTop: 12, borderTop: '1px dashed #E2E8F0', paddingTop: 10 }}>
	                    <Text type="secondary" style={{ fontSize: 12 }}>
	                      Chọn tài khoản chỉ áp cho khách thêm mới. Đổi cho dòng đã có thì bấm:
	                    </Text>
	                    <Button
	                      size="small"
	                      style={{ marginTop: 8 }}
	                      disabled={!selectedAccount}
	                      onClick={() => applyAccountTo("tat_ca")}
	                    >
	                      Áp cho toàn bộ {entries.length} dòng
	                    </Button>

	                    {/* Soát trước khi lưu: tiền của đợt này chảy về đâu, mỗi nơi bao nhiêu dòng */}
	                    <div style={{ marginTop: 10, fontSize: 12 }}>
	                      <Text type="secondary">Tiền sẽ về:</Text>
	                      {accountBreakdown.map((item) => (
	                        <div key={item.accId ?? 'chua-chon'} style={{ marginTop: 4 }}>
	                          <Tag color={item.accId ? 'blue' : 'error'} style={{ marginRight: 6 }}>
	                            {item.soDong} dòng
	                          </Tag>
	                          {item.ten}
	                        </div>
	                      ))}
	                    </div>
	                  </div>
	                )}
	              </Card>

	            </Col>
	          </Row>
              ),
            }]}
          />

          {/* ── Bảng chi tiết khoản thu ── */}
          <Card
            className="collection-section-card collection-card-orange"
            style={{ marginTop: 20 }}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  <span className="collection-card-icon orange">
                    <DollarOutlined />
                  </span>
                  Chi tiết các khoản thu
                  <Text
                    type="secondary"
                    style={{ fontSize: 13, marginLeft: 8 }}
                  >
                    (Giá đã bao gồm thuế)
                  </Text>
                </span>
                <Space wrap>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Hiển thị: <strong>{visibleEntryCount}</strong> /{" "}
                    <strong>{entries.length}</strong> khách hàng
                  </Text>
                </Space>
              </div>
            }>

            {entries.length > 0 && (
              <div className="collection-filter-toolbar">
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                    <Input
                      placeholder="Tìm theo tên, SĐT, tài sản..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      allowClear
                      prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
                    />
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Lọc theo đơn vị"
                      value={filterOrgId}
                      onChange={setFilterOrgId}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={[
                        { value: null, label: "Tất cả đơn vị" },
                        ...uniqueOrgs.map((org) => ({
                          value: org.id,
                          label: org.name,
                        })),
                      ]}
                    />
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                    <Button
                      type={sortByOrg ? "primary" : "default"}
                      icon={<EnvironmentOutlined />}
                      onClick={() => setSortByOrg(!sortByOrg)}
                      style={{ width: "100%" }}
                    >
                      {sortByOrg ? "Đang nhóm" : "Nhóm theo đơn vị"}
                    </Button>
                  </Col>
                  {/* Lọc ra một khối đơn vị rồi áp tài khoản cho đúng khối đó — cách gọn
                      nhất cho đợt thu gồm nhiều khối dùng tài khoản khác nhau. */}
                  <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                    <Button
                      style={{ width: "100%" }}
                      disabled={!selectedAccount || (!filterText && !filterOrgId)}
                      onClick={() => applyAccountTo("dang_loc")}
                    >
                      Áp tài khoản cho dòng đang lọc
                    </Button>
                  </Col>
                  {(zeroEntries.length > 0 || zeroFilterIds) && (
                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                      <ZeroFeeFilterButton
                        count={zeroEntries.length}
                        active={!!zeroFilterIds}
                        onToggle={toggleZero}
                      />
                    </Col>
                  )}
                  <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                    <Button
                      style={{ width: "100%" }}
                      onClick={() => {
                        setFilterText("");
                        setFilterOrgId(null);
                        setSortByOrg(true);
                        clearZero();
                      }}
                      disabled={!filterText && !filterOrgId && !zeroFilterIds && sortByOrg}
                    >
                      Xóa bộ lọc
                    </Button>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                    {(filterText || filterOrgId || zeroFilterIds) && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <FilterOutlined /> Đang lọc
                      </Text>
                    )}
                  </Col>
                </Row>
              </div>
            )}

            {tableDataSource.length > 0 ? (
              (() => {
                const totalWidth = columns.reduce((sum, col) => {
                  const w =
                    typeof col.width === "string"
                      ? parseInt(col.width) || 150
                      : col.width || 150;
                  return sum + w;
                }, 0);
                const rowHeight = 70;
                const maxHeight = tableMaxHeight;
                const listHeight = Math.min(
                  tableDataSource.length * rowHeight,
                  maxHeight,
                );

                return (
                  <div
                    className="collection-table-container"
                    style={{ "--collection-table-width": `${totalWidth + 50}px` }}
                  >
                    <div className="collection-table-header-viewport" ref={tableHeaderRef}>
                      <div
                        className="collection-table-header"
                        style={{ minWidth: totalWidth + 50 }}
                      >
                        {columns.map((col, idx) => (
                          <div
                            key={idx}
                            style={{
                              width:
                                typeof col.width === "string"
                                  ? col.width
                                  : `${col.width}px`,
                              minWidth:
                                typeof col.width === "string"
                                  ? col.width
                                  : `${col.width}px`,
                              flexShrink: 0,
                              textAlign: col.align || "left",
                              padding: "0 8px",
                              borderRight:
                                idx !== columns.length - 1
                                  ? "1px solid #E2E8F0"
                                  : "none",
                              boxSizing: "border-box",
                              // Ô ghim phải cao bằng cả hàng tiêu đề (có ô hai dòng vì
                              // kèm thuế/đơn vị) và mang nền đặc trùng tông tiêu đề,
                              // không thì phần cột trượt qua bên dưới vẫn lòi ra ở
                              // khoảng hở trên và dưới.
                              ...(col.fixed === "left" && {
                                position: "sticky",
                                left: 0,
                                alignSelf: "stretch",
                                display: "flex",
                                alignItems: "center",
                                background: "#eceff4",
                                zIndex: 11,
                                borderRight: "1px solid #cbd5e1",
                                boxShadow: "6px 0 6px -4px rgba(15, 23, 42, 0.12)",
                              }),
                              ...(col.fixed === "right" && {
                                position: "sticky",
                                right: 0,
                                alignSelf: "stretch",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                background: "#eceff4",
                                zIndex: 11,
                                borderLeft: "1px solid #cbd5e1",
                                boxShadow: "-6px 0 6px -4px rgba(15, 23, 42, 0.12)",
                              }),
                            }}
                          >
                            {col.title}
                          </div>
                        ))}
                      </div>

                      </div>

                      <List
                        className="collection-table-body"
                        outerRef={tableBodyRef}
                        height={listHeight}
                        itemCount={tableDataSource.length}
                        itemSize={70}
                        width="100%"
                        itemKey={(index) => tableDataSource[index]?.key || index}
                        itemData={{ tableDataSource, columns }}
                      >
                        {CollectionRow}
                      </List>
                  </div>
                );
              })()
            ) : (
              <div className="collection-empty-state">
                <UserOutlined />
                <h4>Chưa có khách hàng nào</h4>
                <p>Vui lòng chọn đơn vị và thêm khách hàng để bắt đầu</p>
              </div>
            )}

            {/* ✅ Summary bar dùng entryValues */}
            {entries.length > 0 && (
	              <Form.Item
	                shouldUpdate={(prev, curr) => prev.entryData !== curr.entryData}
	                noStyle
	              >
	                {() => {
	                  const { grandSubtotal, grandDiscount, grandTotal } =
	                    getCollectionSummary();
	                  return (
	                    <div className="collection-summary">
                      <Row gutter={[24, 12]} align="middle">
                        <Col xs={24} sm={6}>
                          <div className="collection-summary-stat">
                            <div className="stat-label">Số lượng</div>
                            <div className="stat-value blue">
                              <UserOutlined style={{ marginRight: 4 }} />
                              {entries.length}
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 400,
                                  color: "#94a3b8",
                                  marginLeft: 4,
                                }}
                              >
                                khách hàng
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={6}>
                          <div className="collection-summary-stat">
                            <div className="stat-label">Tổng phí</div>
                            <div className="stat-value green">
                              {grandSubtotal.toLocaleString("vi-VN")} đ
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={6}>
                          <div className="collection-summary-stat">
                            <div className="stat-label">Tổng giảm trừ</div>
                            <div className="stat-value orange">
                              -{grandDiscount.toLocaleString("vi-VN")} đ
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} sm={6}>
                          <div className="collection-summary-highlight">
                            <div className="stat-label">Tổng tiền thu</div>
                            <div className="stat-value">
                              <DollarOutlined style={{ marginRight: 4 }} />
                              {grandTotal.toLocaleString("vi-VN")} đ
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                }}
              </Form.Item>
            )}
          </Card>

          </div>{/* /Bước 2 */}

          {/* ── Bước 3: Xác nhận & Lưu ── */}
          <div style={{ display: currentStep !== 2 ? 'none' : 'block' }}>
            <Card
              title={
                <span>
                  <span className="collection-card-icon blue"><CalendarOutlined /></span>
                  Xác nhận đợt thu
                </span>
              }
              className="collection-section-card collection-card-blue"
            >
              <Form.Item shouldUpdate>
	                {({ getFieldValue }) => {
	                  const name = getFieldValue('name');
	                  const categoryId = getFieldValue('collection_category_id');
	                  const catObj = categories.find(c => c.id === categoryId);
	                  const statusVal = getFieldValue('status');
	                  const statusLabel = statusVal === 'PLANNED' ? 'Nháp' : 'Đang hoạt động';
	                  const dateRange = getFieldValue('dateRange');
	                  const isLocked = getFieldValue('is_locked') ?? true;
	                  const transferContent = getFieldValue('transfer_content');
	                  const description = getFieldValue('description');
	                  const { grandSubtotal, grandDiscount, grandTotal } =
	                    getCollectionSummary();
	                  const rowSt = { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F8FAFC' };
	                  const lbSt = { color: '#64748B', fontSize: 13 };
	                  const vlSt = { fontWeight: 500, fontSize: 13 };
	                  return (
                    <Row gutter={[32, 0]}>
                      <Col xs={24} md={15}>
                        <div style={rowSt}>
                          <span style={lbSt}>Tên đợt thu</span>
                          <span style={vlSt}>{name || '—'}</span>
                        </div>
                        {catObj && <div style={rowSt}><span style={lbSt}>Loại phí</span><span style={vlSt}>{catObj.name}</span></div>}
                        <div style={rowSt}>
                          <span style={lbSt}>Trạng thái</span>
                          <span style={{ ...vlSt, color: statusVal === 'ONGOING' ? '#128555' : '#475569' }}>{statusLabel}</span>
                        </div>
                        <div style={rowSt}>
                          <span style={lbSt}>Quyền sửa cấp con</span>
                          <span style={vlSt}>{isLocked ? 'Chỉ tài khoản cha được sửa' : 'Cho phép cấp con chỉnh sửa'}</span>
                        </div>
                        {dateRange?.[0] && <div style={rowSt}><span style={lbSt}>Thời gian</span><span style={vlSt}>{dateRange[0].format('DD/MM/YYYY')} → {dateRange?.[1]?.format('DD/MM/YYYY')}</span></div>}
                        {transferContent && (
                          <div style={rowSt}>
                            <span style={lbSt}>Nội dung chuyển khoản</span>
                            <span style={{ ...vlSt, fontFamily: 'monospace', background: '#F8FAFC', padding: '2px 8px', borderRadius: 4 }}>{transferContent}</span>
                          </div>
                        )}
                        {description && <div style={{ ...rowSt, borderBottom: 'none' }}><span style={lbSt}>Mô tả</span><span style={{ ...vlSt, maxWidth: '60%', textAlign: 'right' }}>{description}</span></div>}
                      </Col>
	                      <Col xs={24} md={9}>
	                        <div className="collection-summary" style={{ marginTop: 0 }}>
	                          <div className="collection-summary-stat">
	                            <div className="stat-label">Số lượng</div>
	                            <div className="stat-value blue">
	                              <UserOutlined style={{ marginRight: 4 }} />{entries.length}
	                              <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>khách hàng</span>
	                            </div>
	                          </div>
	                          <div className="collection-summary-stat">
	                            <div className="stat-label">Tổng phí</div>
	                            <div className="stat-value green">{grandSubtotal.toLocaleString('vi-VN')} đ</div>
	                          </div>
	                          <div className="collection-summary-stat">
	                            <div className="stat-label">Tổng giảm trừ</div>
	                            <div className="stat-value orange">-{grandDiscount.toLocaleString('vi-VN')} đ</div>
	                          </div>
	                          <div className="collection-summary-highlight">
	                            <div className="stat-label">Tổng tiền thu</div>
	                            <div className="stat-value"><DollarOutlined style={{ marginRight: 4 }} />{grandTotal.toLocaleString('vi-VN')} đ</div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>
            </Card>
          </div>{/* /Bước 3 */}

          {/* ── Navigation buttons (step-aware) ── */}
          <div className="collection-actions">
            <Row justify="space-between" align="middle">
              <Col>
                {currentStep === 0 ? (
	                  <Button htmlType="button" icon={<ArrowLeftOutlined />} onClick={handleBack} className="collection-btn-back">
	                    Quay lại
	                  </Button>
	                ) : (
	                  <Button htmlType="button" icon={<ArrowLeftOutlined />} onClick={handlePrevStep} className="collection-btn-back">
	                    Bước trước
	                  </Button>
	                )}
              </Col>
              {/* Số dòng và tổng tiền ngay trên thanh nút: bước 2 bảng dài, bản cũ phải
                  cuộn ngược lên khối tổng hợp mới đối chiếu được. */}
              <Col flex="auto">
                {currentStep > 0 && entries.length > 0 && (
                  <span className="collection-actions-summary">
                    <span><strong>{entries.length.toLocaleString("vi-VN")}</strong> dòng</span>
                    <span className="sep">·</span>
                    <span>Tổng <strong>{getCollectionSummary().grandTotal.toLocaleString("vi-VN")} đ</strong></span>
                  </span>
                )}
              </Col>
              <Col>
                <Space>
                  {currentStep === 0 && (
	                    <Button htmlType="button" type="default" onClick={handleReset} className="collection-btn-reset">
	                      Làm mới
	                    </Button>
	                  )}
	                  {currentStep < 2 ? (
	                    <Button
	                      key="next-step"
	                      htmlType="button"
	                      type="primary"
	                      icon={<ArrowRightOutlined />}
	                      onClick={handleNextStep}
	                    >
	                      Tiếp theo
	                    </Button>
	                  ) : (
	                    <Button
	                      key="submit-step"
	                      type="primary"
	                      htmlType="submit"
                      loading={submitting}
                      icon={<SaveOutlined />}
                      className="collection-btn-submit"
                    >
                      {submitting ? "Đang lưu..." : "Lưu thông tin"}
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>

          {feeNameModalVisible && FeeNameEditModal}
        </Form>
      </Spin>

      {templateModalVisible && (
        <FeeTemplateModal
          visible={templateModalVisible}
          onCancel={() => setTemplateModalVisible(false)}
          onSuccess={handleTemplateSelect}
        />
      )}
    </div>
  );
}
